import { soundService } from '../services/soundService';
import { FlashcardImage } from '../services/flashcardImageService';

import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { Filter, Send, ChevronDown, ArrowLeftRight,
  Brain, Star, ChevronRight, ArrowLeft as ArrowLeftIcon, ChevronLeft,
  Volume2, Trash2, Sun, Moon, Loader2, Download, FileUp,
  AlertCircle, X, Edit3, AlertTriangle, Check, BarChart2,
  Calendar, Zap, Target, Flame, Settings, RotateCcw, Plus, Store, Eye, EyeOff, Square, MoreVertical
 } from 'lucide-react';
import { createPortal } from 'react-dom';
import { XAxis, YAxis, Tooltip, ResponsiveContainer, BarChart, Bar, Cell } from 'recharts';
import { useNavigate, useLocation, useSearchParams, useParams } from 'react-router-dom';
import { supabase } from '../services/supabaseClient';
import { dbManager } from '../services/db_manager';
import { localFlashcardDb } from '../services/flashcardLocalDb';
import { motion, AnimatePresence } from 'framer-motion';
import { flashcardManager, Flashcard } from '../services/flashcardManager';
import { exerciseProgressService } from '../services/exerciseProgressService';
import { textToSpeechService } from '../services/textToSpeechService';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { useData } from '../contexts/DataContext';
import { useTranslation } from 'react-i18next';
import { useStreakCounter } from '../hooks/useStreakCounter';
import { getLanguageName, getLanguageCode } from '../services/languageConstants';
import { STORE_TRANSLATIONS } from './FlashcardStorePage';
import { getLocalISOString } from '../utils/timezoneUtils';
import { importRateLimiter } from '../utils/importRateLimit';
import { statsService } from '../services/statsService';
import { activeStudyTracker } from '../services/activeStudyTrackerService';
import { tourService } from '../services/tourService';
import { flashcardImportQueueService } from '../services/flashcardImportQueueService';
import { WatchdogGuard } from '../components/WatchdogGuard';
import { FlashcardSkeleton } from '../components/SkeletonLoader';
import { TxtImportModal } from '../components/TxtImportModal';
import { streakService } from '../services/streakService';
import { STRICT_LANGUAGES } from '../constants';
import { uniqueWordsService } from '../services/uniqueWordsService';
import { useUnifiedDictionary } from '../hooks/useUnifiedDictionary';
import { AppBackButton, goBackOr, canGoBack } from '../components/AppBackButton';
import { StudyPageHeader } from '../components/StudyPageHeader';
import { AnimatedIcon, IconMotion } from '../components/AnimatedIcon';
import T from '../components/T';
import i18nInstance from '../i18n';



const audioRef_initial = null; // For type inference if needed, but we use useRef

const LANGUAGE_FLAGS: Record<string, string> = {
  'af': '🇿🇦', // Africâner
  'de': '🇩🇪', // Alemão
  'ar': '🇸🇦', // Árabe
  'arp': '🏳️', // Arapaho (Generic flag or specific if available)
  'br': '🇫🇷', // Bretão (Region in France)
  'bg': '🇧🇬', // Búlgaro
  'zh': '🇨🇳', // Chinês
  'da': '🇩🇰', // Dinamarquês
  'es': '🇪🇸', // Espanhol
  'eo': '🌍', // Esperanto
  'fi': '🇫🇮', // Finlandês
  'fr': '🇫🇷', // Francês
  'fy': '🇳🇱', // Frisão (Netherlands/Germany)
  'fur': '🇮🇹', // Friulano (Italy)
  'gl': '🇪🇸', // Galego (Spain)
  'cy': 'gb-wls', // Galês (Wales - Emoji might not exist, using generic or GB) -> 🏴󠁧󠁢󠁷󠁬󠁳󠁿
  'el': '🇬🇷', // Grego
  'he': '🇮🇱', // Hebraico
  'nl': '🇳🇱', // Holandês
  'hu': '🇭🇺', // Húngaro
  'en': '🇺🇸', // Inglês
  'ia': '🌐', // Interlíngua
  'ga': '🇮🇪', // Irlandês
  'is': '🇮🇸', // Islandês
  'it': '🇮🇹', // Italiano
  'ja': '🇯🇵', // Japonês
  'la': '🏛️', // Latim
  'pl': '🇵🇱', // Polonês
  'pt': '🇧🇷', // Português
  'ru': '🇷🇺', // Russo
  'sa': '🕉️', // Sânscrito
  'sv': '🇸🇪', // Sueco
  'tl': '🇵🇭'  // Tagalo
};

const LANGUAGE_NAME_TO_CODE: Record<string, string> = {
  'inglês': 'en', 'english': 'en', 'português': 'pt', 'portuguese': 'pt',
  'francês': 'fr', 'french': 'fr', 'espanhol': 'es', 'spanish': 'es',
  'italiano': 'it', 'italian': 'it', 'alemão': 'de', 'german': 'de',
  'holandês': 'nl', 'dutch': 'nl', 'russo': 'ru', 'russian': 'ru',
  'japonês': 'ja', 'japanese': 'ja', 'chinês': 'zh', 'chinese': 'zh',
  'árabe': 'ar', 'arabic': 'ar'
};

/**
 * Mapeia códigos ISO curtos para localidades de voz completas (sotaques).
 */
const ISO_TO_LOCALE: Record<string, string> = {
  'af': 'af-ZA', 'de': 'de-DE', 'ar': 'ar-SA', 'arp': 'en-US', // Arapaho fallback
  'br': 'fr-FR', // Bretão fallback
  'bg': 'bg-BG', 'zh': 'zh-CN', 'da': 'da-DK', 'es': 'es-ES', 'eo': 'eo', // Esperanto might need specific handling
  'fi': 'fi-FI', 'fr': 'fr-FR', 'fy': 'nl-NL', // Frisão fallback
  'fur': 'it-IT', // Friulano fallback
  'gl': 'es-ES', // Galego fallback
  'cy': 'cy-GB', 'el': 'el-GR', 'he': 'he-IL', 'nl': 'nl-NL', 'hu': 'hu-HU',
  'en': 'en-US', 'ia': 'ia', 'ga': 'ga-IE', 'is': 'is-IS', 'it': 'it-IT',
  'ja': 'ja-JP', 'la': 'la', 'pl': 'pl-PL', 'pt': 'pt-BR', 'ru': 'ru-RU',
  'sa': 'hi-IN', // Sânscrito fallback to Hindi if needed
  'sv': 'sv-SE', 'tl': 'tl-PH'
};

const detectLanguageAndSpeak = async (text: string, forceLocale?: string) => {
  const locale = forceLocale || 'en-US';
  await textToSpeechService.speak(text, locale, 0.75);
};
const ConfirmationModal: React.FC<{
  isOpen: boolean;
  title: string;
  message: string;
  onConfirm: () => void;
  onCancel: () => void;
  confirmLabel?: string;
  isDanger?: boolean;
}> = ({ isOpen, title, message, onConfirm, onCancel, confirmLabel, isDanger = false }) => {
  const { t } = useTranslation();
  const safeT = (key: string, fallback: string) => {
    const res = t(key);
    return res === key ? fallback : res;
  };
  const finalConfirmLabel = confirmLabel || safeT('common.confirm', 'Confirmar');
  if (!isOpen) return null;
  return (
    <div className="fixed inset-0 z-[200] flex items-center justify-center p-6 bg-slate-950/60 backdrop-blur-sm animate-in fade-in duration-200">
      <div className="bg-surface p-8 rounded-[28px] border border-black/[0.06] dark:border-white/[0.06] w-[85vw] max-w-md animate-in zoom-in-95 duration-200">
        <div className={`w-12 h-12 rounded-2xl mb-6 flex items-center justify-center ${isDanger ? 'bg-tint-rose text-bad dark:bg-tint-rose/20' : 'bg-primary/10 text-primary'}`}>
          <AlertCircle size={24} />
        </div>
        <h3 className="text-lg font-semibold dark:text-ink mb-2">{title}</h3>
        <p className="text-slate-500 dark:text-slate-400 text-sm mb-8 leading-relaxed font-medium">{message}</p>
        <div className="grid grid-cols-2 gap-3">
          <button onClick={onCancel} className="text-[13px] font-medium h-12 bg-tint-stone/60 text-muted hover:text-ink dark:text-slate-400 rounded-full hover:bg-tint-stone transition-all">{safeT('common.cancel', 'Cancelar')}</button>
          <button onClick={onConfirm} className={`py-4 ${isDanger ? 'bg-bad' : 'bg-ink'} text-white rounded-2xl font-normal text-[10px] uppercase font-mono tracking-[0.14em] hover:opacity-90 transition-all active:scale-95`}>{finalConfirmLabel}</button>
        </div>
      </div>
    </div>
  );
};

const InputModal: React.FC<{
  isOpen: boolean;
  title: string;
  placeholder: string;
  initialValue: string;
  onSave: (value: string) => void;
  onCancel: () => void;
}> = ({ isOpen, title, placeholder, initialValue, onSave, onCancel }) => {
  const [value, setValue] = useState(initialValue);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (isOpen) {
      setValue(initialValue);
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [isOpen, initialValue]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[200] flex items-center justify-center p-6 bg-slate-950/60 backdrop-blur-sm animate-in fade-in duration-200">
      <div className="bg-surface p-8 rounded-[28px] border border-black/[0.06] dark:border-white/[0.06] w-[85vw] max-w-md animate-in zoom-in-95 duration-200" onClick={(e) => e.stopPropagation()}>
        <div className="w-12 h-12 rounded-2xl bg-primary/10 text-primary mb-6 flex items-center justify-center">
          <Edit3 size={24} />
        </div>
        <h3 className="text-lg font-semibold dark:text-ink mb-6">{title}</h3>
        <input
          ref={inputRef}
          type="text"
          value={value}
          onChange={(e) => setValue(e.target.value)}
          placeholder={placeholder}
          className="w-full bg-slate-100 dark:bg-slate-800 border-none rounded-2xl px-6 py-4 mb-8 font-bold text-ink placeholder:text-slate-400 outline-none ring-2 ring-transparent focus:ring-primary/30 transition-all"
        />
        <div className="grid grid-cols-2 gap-3">
          <button onClick={onCancel} className="text-[13px] font-medium h-12 bg-tint-stone/60 text-muted hover:text-ink dark:text-slate-400 rounded-full hover:bg-tint-stone transition-all">Cancelar</button>
          <button onClick={() => onSave(value)} className="text-[13px] font-medium h-12 bg-primary text-slate-900 rounded-full hover:opacity-90 transition-all active:scale-95">Salvar</button>
        </div>
      </div>
    </div>
  );
};

// ── Dashboard Components ─────────────────
const FORMAT_NUMBER = (num: number) => {
  return num.toString();
};

const DASHBOARD_STRINGS: Record<string, Record<string, string>> = {
  pt: {
    mediaLabel: 'Média',
    mediaTooltip: 'Total de cards revisados no período dividido pelo número de dias. Mostra sua intensidade média de estudo.',
    checkinsLabel: 'Check-ins',
    checkinsTooltip: 'Quantos dias únicos você estudou neste período. Mostra sua constância.',
    recordeLabel: 'Recorde',
    recordeTooltip: 'Seu recorde pessoal do máximo de flashcards em que você estudou neste período.',
    recordeDatePrefix: 'Recorde obtido na data: ',
    revisoesLabel: 'Revisões',
    revisoesTooltip: 'Quantidade de revisões de flashcards realizadas no período selecionado.',
    totalLabel: 'TOTAL',
    totalTooltip: 'Quantos flashcards você estudou no período selecionado (soma de flashcards novos e revisados).',
  },
  en: {
    mediaLabel: 'Average',
    mediaTooltip: 'Total cards reviewed in the period divided by the number of days. Shows your study intensity.',
    checkinsLabel: 'Check-ins',
    checkinsTooltip: 'How many unique days you studied in this period. Shows your consistency.',
    recordeLabel: 'Record',
    recordeTooltip: 'Your personal record for the maximum flashcards studied in this period.',
    recordeDatePrefix: 'Record achieved on date: ',
    revisoesLabel: 'Reviews',
    revisoesTooltip: 'Number of flashcard reviews completed in the selected period.',
    totalLabel: 'TOTAL',
    totalTooltip: 'How many flashcards you studied in the selected period (sum of new and reviewed flashcards).',
  },
  es: {
    mediaLabel: 'Promedio',
    mediaTooltip: 'Total de tarjetas revisadas en el período dividido por el número de días.',
    checkinsLabel: 'Check-ins',
    checkinsTooltip: 'Cuántos días únicos estudiaste en este período. Muestra tu constancia.',
    recordeLabel: 'Récord',
    recordeTooltip: 'Tu récord personal del máximo de flashcards que estudiaste en este período.',
    recordeDatePrefix: 'Récord obtenido en la fecha: ',
    revisoesLabel: 'Revisiones',
    revisoesTooltip: 'Cantidad de revisiones de flashcards realizadas en el período seleccionado.',
    totalLabel: 'TOTAL',
    totalTooltip: 'Cuántas flashcards estudiaste en el período seleccionado (suma de flashcards nuevas y revisadas).',
  },
  fr: {
    mediaLabel: 'Moyenne',
    mediaTooltip: 'Total des cartes révisées au cours de la période divisé par le nombre de jours.',
    checkinsLabel: 'Check-ins',
    checkinsTooltip: 'Nombre de jours uniques où vous avez étudié au cours de cette période.',
    recordeLabel: 'Record',
    recordeTooltip: 'Votre record personnel du nombre maximum de flashcards étudiées au cours de cette période.',
    recordeDatePrefix: 'Record obtenu le : ',
    revisoesLabel: 'Révisions',
    revisoesTooltip: 'Nombre de révisions de flashcards effectuées au cours de la période sélectionnée.',
    totalLabel: 'TOTAL',
    totalTooltip: 'Combien de flashcards vous avez étudiées au cours de la période sélectionnée (somme des nouvelles et révisées).',
  },
  de: {
    mediaLabel: 'Durchschnitt',
    mediaTooltip: 'Gesamtzahl der im Zeitraum überprüften Karten geteilt durch die Anzahl der Tage.',
    checkinsLabel: 'Check-ins',
    checkinsTooltip: 'Wie viele einzelne Tage Sie in diesem Zeitraum gelernt haben.',
    recordeLabel: 'Rekord',
    recordeTooltip: 'Ihr persönlicher Rekord für die maximale Anzahl der in diesem Zeitraum gelernten Karteikarten.',
    recordeDatePrefix: 'Rekord erzielt am Datum: ',
    revisoesLabel: 'Wiederholungen',
    revisoesTooltip: 'Anzahl der im ausgewählten Zeitraum durchgeführten Karteikarten-Wiederholungen.',
    totalLabel: 'GESAMT',
    totalTooltip: 'Wie viele Karteikarten Sie im ausgewählten Zeitraum gelernt haben (Summe aus neuen und wiederholten Karteikarten).',
  },
  it: {
    mediaLabel: 'Media',
    mediaTooltip: 'Totale schede ripassate nel periodo diviso per il numero di giorni.',
    checkinsLabel: 'Check-in',
    checkinsTooltip: 'Quanti giorni unici hai studiato in questo periodo.',
    recordeLabel: 'Record',
    recordeTooltip: 'Il tuo record personale per il massimo numero di flashcard studiate in questo periodo.',
    recordeDatePrefix: 'Record ottenuto in data: ',
    revisoesLabel: 'Revisioni',
    revisoesTooltip: 'Numero di revisioni di flashcard effettuate nel periodo selezionato.',
    totalLabel: 'TOTALE',
    totalTooltip: 'Quante flashcard hai studiato nel periodo selezionato (somma di flashcard nuove e ripassate).',
  },
  ru: {
    mediaLabel: 'Среднее',
    mediaTooltip: 'Всего карточек за период, деленное на количество дней.',
    checkinsLabel: 'Отметки',
    checkinsTooltip: 'Сколько уникальных дней вы учились в этот период.',
    recordeLabel: 'Рекорд',
    recordeTooltip: 'Ваш личный рекорд по максимальному количеству карточек, изученных за этот период.',
    recordeDatePrefix: 'Рекорд получен: ',
    revisoesLabel: 'Повторения',
    revisoesTooltip: 'Количество повторений карточек за выбранный период.',
    totalLabel: 'ВСЕГО',
    totalTooltip: 'Сколько карточек вы изучили за выбранный период (сумма новых и повторенных).',
  },
  zh: {
    mediaLabel: '平均',
    mediaTooltip: '期间复习的卡片总数除以天数。',
    checkinsLabel: '打卡',
    checkinsTooltip: '您在此期间学习的独特天数。',
    recordeLabel: '记录',
    recordeTooltip: '您在此期间学习的最大闪卡数量个人记录。',
    recordeDatePrefix: '记录获得日期：',
    revisoesLabel: '复习',
    revisoesTooltip: '在选定期间内完成的闪卡复习次数。',
    totalLabel: '总计',
    totalTooltip: '您在所选期间学习了多少张闪卡（新闪卡和复习闪卡的总和）。',
  }
};

const StudyDashboard: React.FC<{ logs: any[], totalReviews: number, profile?: any, availableLangs: any[], availableDecks: any[], filterLang: string, setFilterLang: (v: string) => void, filterDeck: string, setFilterDeck: (v: string) => void, filterPeriod: string, setFilterPeriod: (v: 'Anos' | 'Semanas' | 'Dias') => void, isLoading?: boolean }> = ({ logs, totalReviews, profile, availableLangs, availableDecks, filterLang, setFilterLang, filterDeck, setFilterDeck, filterPeriod, setFilterPeriod, isLoading = false }) => {
  const { t, i18n } = useTranslation();
  const safeT = (key: string, fallback: string) => { const res = t(key); return res === key ? fallback : res; };
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [tempLang, setTempLang] = useState(filterLang);
  const [tempDeck, setTempDeck] = useState(filterDeck);
  const [tempPeriod, setTempPeriod] = useState(filterPeriod);

  const uiLang = (i18n.language || 'pt').split('-')[0].toLowerCase();
  const dashTrans = DASHBOARD_STRINGS[uiLang] || DASHBOARD_STRINGS.pt;

  useEffect(() => {
    if (!isFilterOpen) {
      setTempLang(filterLang);
      setTempDeck(filterDeck);
      setTempPeriod(filterPeriod);
    }
  }, [isFilterOpen, filterLang, filterDeck, filterPeriod]);

  const handleApplyFilters = () => {
    setFilterLang(tempLang);
    setFilterDeck(tempDeck);
    setFilterPeriod(tempPeriod as 'Anos' | 'Semanas' | 'Dias');
  };

  const chartData = useMemo(() => {
    const sortedLogs = [...logs].sort((a, b) => (a.activity_date || '').localeCompare(b.activity_date || ''));
    const last8 = sortedLogs.slice(-8);
    while (last8.length < 8) last8.unshift({ activity_date: '', cards_reviewed: 0 });
    return last8.map(l => {
      let fd = '---';
      if (l.activity_date) {
        if (filterPeriod === 'Anos') { const p = l.activity_date.split('-'); fd = `${p[1] || ''}/${(p[0] || '').slice(-2)}`; }
        else if (filterPeriod === 'Semanas') { const p = l.activity_date.split('-'); fd = `Sem ${p[1] || ''}`; }
        else { const d = new Date(l.activity_date + 'T12:00:00'); fd = `${d.getDate().toString().padStart(2,'0')} ${d.toLocaleDateString(i18n.language || 'pt-BR',{month:'short'}).replace('.','')} `; }
      }
      return { name: fd, value: l.cards_reviewed || 0 };
    });
  }, [logs, filterPeriod, i18n.language]);

  const stats = useMemo(() => {
    const activeLogs = logs.filter((l: any) => (l.cards_reviewed || 0) > 0);
    const latestLog = logs[0] || null;

    // 1. CHECK-INS (Dias: 1 or 0; Semanas: 0 to 7; Anos: total unique days in year)
    let checkins = 0;
    if (filterPeriod === 'Dias') {
      const hasActivityToday = logs.some((l: any) => (l.cards_reviewed || 0) > 0);
      checkins = hasActivityToday ? 1 : 0;
    } else if (filterPeriod === 'Semanas') {
      checkins = latestLog?.unique_days !== undefined 
        ? Math.min(latestLog.unique_days, 7)
        : Math.min(activeLogs.length, 7);
    } else {
      // Anos: sum of unique study days across all logs for the year
      checkins = activeLogs.reduce((sum, l) => sum + (l.unique_days || 1), 0);
    }

    // 2. MÉDIA (Cards per day/elapsed unit)
    let avg = 0;
    const totalPeriodCards = latestLog?.cards_reviewed ?? logs.reduce((acc, l) => acc + (l.cards_reviewed || 0), 0);

    if (filterPeriod === 'Dias') {
      avg = totalPeriodCards;
    } else if (filterPeriod === 'Semanas') {
      const dayOfWeek = new Date().getDay() || 7; // 1 to 7
      avg = Math.round(totalPeriodCards / Math.max(1, dayOfWeek));
    } else {
      // Anos
      const startOfYear = new Date(new Date().getFullYear(), 0, 1);
      const dayOfYear = Math.ceil((new Date().getTime() - startOfYear.getTime()) / (1000 * 60 * 60 * 24)) || 1;
      avg = Math.round(totalPeriodCards / Math.max(1, dayOfYear));
    }

    // 3. RECORDE (Max cards studied in any single period unit)
    let maxRecordValue = 0;
    let maxRecordDate = '';
    logs.forEach((l: any) => {
      const val = l.cards_reviewed || 0;
      if (val >= maxRecordValue) {
        maxRecordValue = val;
        if (l.activity_date) {
          maxRecordDate = l.activity_date;
        }
      }
    });

    // 4. REVISÕES (Review attempts logged)
    const sumReviews = latestLog?.cards_reviewed ?? logs.reduce((acc: number, l: any) => acc + (l.cards_reviewed || 0), 0);

    // 5. TOTAL (Total cards reviewed in period)
    const sumTotalCards = latestLog?.cards_reviewed ?? logs.reduce((acc: number, l: any) => acc + (l.cards_reviewed || 0), 0);

    return {
      avg,
      checkins,
      recordeValue: maxRecordValue,
      recordeDate: maxRecordDate,
      sumReviews,
      sumTotalCards
    };
  }, [logs, filterPeriod]);

  return (
    <div data-tour="flashcards-dashboard" className={`bg-surface dark:bg-slate-950 rounded-[28px] p-8 border border-black/[0.06] dark:border-white/[0.06] space-y-8 animate-in fade-in zoom-in-95 duration-500 mb-8 mt-4 relative overflow-hidden group transition-all duration-300 ${isLoading ? 'backdrop-blur-md opacity-60 pointer-events-none' : ''}`}>
      {isLoading && (
        <div className="absolute inset-0 z-[100] flex items-center justify-center bg-slate-900/30 backdrop-blur-sm rounded-[28px] animate-in fade-in duration-200">
          <Loader2 size={36} className="text-primary animate-spin" />
        </div>
      )}
      <div className="absolute -top-10 -right-10 w-40 h-40 bg-primary/10 blur-[5rem] rounded-full group-hover:bg-primary/20 transition-all duration-700" />
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 relative z-10">
        <div className="flex items-center gap-3">
          <div className="p-3 bg-primary/20 text-primary rounded-2xl"><BarChart2 size={24} /></div>
          <div>
            <h3 className="text-lg font-semibold text-ink leading-tight">{safeT('flashcards.dashboardTitle', 'Dashboard de Progresso')}</h3>
            <p className="text-[10px] font-normal uppercase font-mono text-slate-400 dark:text-slate-500 tracking-[0.14em] mt-0.5">{safeT('flashcards.studyIntensity', 'Intensidade de Estudos')}</p>
          </div>
        </div>
        <div className="relative">
          <button onClick={() => setIsFilterOpen(true)} className="p-3 rounded-full bg-slate-100 dark:bg-slate-800/60 hover:bg-slate-200 dark:hover:bg-slate-800 text-slate-500 dark:text-slate-300 border border-black/[0.06] dark:border-slate-700/60 backdrop-blur-md transition-all cursor-pointer flex items-center justify-center" title="Filtrar">
            <Filter size={18} />
          </button>
          {isFilterOpen && typeof document !== 'undefined' && createPortal(
            <div className="fixed inset-0 z-[99999999] flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm" onClick={() => setIsFilterOpen(false)}>
              <div className="bg-surface border border-black/[0.06] dark:border-white/[0.06] rounded-[24px] p-6 flex flex-col gap-5 animate-in zoom-in-95 duration-200 w-full max-w-sm" onClick={(e) => e.stopPropagation()}>
                <div className="flex justify-between items-center">
                  <h4 className="text-xs font-normal uppercase font-mono tracking-[0.14em] text-slate-400"><T k="ui.fc.dynamicFilters" /></h4>
                  <button onClick={() => setIsFilterOpen(false)} className="text-slate-400 hover:text-slate-600 dark:hover:text-white p-1 bg-slate-100 dark:bg-slate-800 rounded-full transition-colors"><X size={14}/></button>
                </div>
                <div className="space-y-2">
                  <label className="text-[10px] font-normal uppercase font-mono text-slate-500 tracking-[0.14em]">Agrupamento</label>
                  <div className="bg-slate-100 dark:bg-slate-800 p-1 rounded-xl flex items-center">
                    {(['Dias', 'Semanas', 'Anos'] as const).map(f => (<button key={f} onClick={() => setTempPeriod(f)} className={`flex-1 py-2.5 rounded-lg text-[10px] font-normal uppercase font-mono tracking-[0.14em] transition-all ${tempPeriod === f ? 'bg-surface dark:bg-slate-700 text-primary shadow' : 'text-slate-500 hover:text-slate-300'}`}>{f}</button>))}
                  </div>
                </div>
                <div className="space-y-2">
                  <label className="text-[10px] font-normal uppercase font-mono text-slate-500 tracking-[0.14em]">Idioma</label>
                  <select value={tempLang} onChange={(e) => { setTempLang(e.target.value); setTempDeck('all'); }} className="w-full bg-slate-50 dark:bg-slate-800 border border-black/[0.06] dark:border-slate-700 rounded-xl px-4 py-3 text-sm font-bold text-slate-700 dark:text-slate-200 outline-none appearance-none cursor-pointer">
                    <option value="all"><T k="online_library.all_languages" /></option>
                    {availableLangs.map((l: any) => (<option key={l.code} value={l.code}>{l.name}</option>))}
                  </select>
                </div>
                <div className="space-y-2">
                  <label className="text-[10px] font-normal uppercase font-mono text-slate-500 tracking-[0.14em]">Baralho</label>
                  <select value={tempDeck} onChange={(e) => setTempDeck(e.target.value)} className="w-full bg-slate-50 dark:bg-slate-800 border border-black/[0.06] dark:border-slate-700 rounded-xl px-4 py-3 text-sm font-bold text-slate-700 dark:text-slate-200 outline-none appearance-none cursor-pointer">
                    <option value="all"><T k="ui.fc.allDecks" /></option>
                    {availableDecks.filter((d: any) => tempLang === 'all' || d.lang_code === tempLang).map((d: any) => (<option key={d.id} value={d.id}>{d.name}</option>))}
                  </select>
                </div>
                <button 
                  onClick={() => {
                    handleApplyFilters();
                    setIsFilterOpen(false);
                  }}
                  className="w-full mt-2 bg-ok hover:bg-ok text-surface font-normal py-3 rounded-xl text-xs uppercase font-mono tracking-[0.14em] font-normal transition-all"
                >
                  Aplicar Filtros
                </button>
              </div>
            </div>,
            document.body
          )}
        </div>
      </div>
      <div className="w-full h-48 min-h-[192px] mt-4 relative flex items-center justify-center bg-slate-50/50 dark:bg-slate-800/20 rounded-3xl overflow-hidden border border-black/[0.06] dark:border-white/[0.06]">
        <ResponsiveContainer width="99%" height="100%" minWidth={1} minHeight={1} initialDimension={{ width: 300, height: 192 }}>
          <BarChart data={chartData} margin={{ top: 20, right: 10, left: -20, bottom: 0 }}>
            <XAxis dataKey="name" axisLine={false} tickLine={false} tick={false} dy={15} />
            <Tooltip cursor={{ fill: 'rgba(255,255,255,0.05)', radius: 8 }} content={({ active, payload }) => { if (active && payload && payload.length && payload[0].payload.name !== '---') { return (<div className="bg-slate-800 text-white p-4 rounded-3xl border border-white/10 animate-in zoom-in-95 duration-150"><p className="text-[10px] font-normal uppercase font-mono tracking-[0.14em] text-slate-400 mb-2">{payload[0].payload.name}</p><p className="text-sm font-semibold text-white">{payload[0].value} Cards</p></div>); } return null; }} />
            <Bar dataKey="value" radius={[10, 10, 10, 10]} barSize={28}>
              {chartData.map((entry, index) => { const c = entry.value >= 15 ? '#8E9668' : entry.value >= 8 ? '#8E9668' : entry.value > 0 ? '#8E9668' : '#252F3E'; return <Cell key={`cell-${index}`} fill={c} className="transition-all duration-500" />; })}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>
      <div className="grid grid-cols-3 sm:grid-cols-5 gap-3 sm:gap-4 pt-6 border-t border-ink/[0.06]">
        <StatCard icon={<Zap size={16} />} label={dashTrans.mediaLabel} tint="bg-tint-sky" motionKind="pulse" index={0} value={stats.avg} subtitle={safeT('flashcards.cardsPerDay', 'Cards/Dia')} tooltip={dashTrans.mediaTooltip} />
        <StatCard icon={<Calendar size={16} />} label={dashTrans.checkinsLabel} tint="bg-tint-sage" motionKind="bounce" index={1} value={stats.checkins} subtitle={safeT('flashcards.activeDays', 'Dias Ativos')} tooltip={dashTrans.checkinsTooltip} />
        <StatCard 
          icon={<Target size={16} />} 
          label={dashTrans.recordeLabel} tint="bg-tint-butter" motionKind="pop" index={2} 
          value={stats.recordeValue} 
          subtitle={dashTrans.recordeLabel} 
          tooltipTitle={dashTrans.recordeLabel}
          tooltip={dashTrans.recordeTooltip} 
          extraInfo={stats.recordeDate ? `${dashTrans.recordeDatePrefix}${stats.recordeDate}` : undefined}
        />
        <StatCard icon={<Star size={16} />} label={dashTrans.revisoesLabel} tint="bg-tint-lavender" motionKind="spin" index={3} value={FORMAT_NUMBER(stats.sumReviews)} subtitle={dashTrans.revisoesLabel} tooltip={dashTrans.revisoesTooltip} />
        <StatCard icon={<Flame size={16} />} label={dashTrans.totalLabel} tint="bg-tint-peach" motionKind="wobble" index={4} value={FORMAT_NUMBER(stats.sumTotalCards)} subtitle={dashTrans.totalLabel} tooltip={dashTrans.totalTooltip} />
      </div>
    </div>
  );
};

const StatCard: React.FC<{ icon: React.ReactNode, label: string, value: string | number, subtitle: string, tooltipTitle?: string, tooltip?: string, extraInfo?: string, tint?: string, motionKind?: IconMotion, index?: number }> = ({ icon, label, value, subtitle, tooltipTitle, tooltip, extraInfo, tint = 'bg-tint-stone', motionKind = 'pop', index = 0 }) => {
  // Design minimal v1: indicador em orbe; ao clicar, expande numa janela com a explicação completa
  const [showTooltip, setShowTooltip] = useState(false);
  const canExpand = !!(tooltip || extraInfo);
  return (
  <>
    <motion.button
      type="button"
      onClick={(e) => { e.stopPropagation(); if (canExpand) setShowTooltip(true); }}
      initial={{ opacity: 0, scale: 0.9 }}
      animate={{ opacity: 1, scale: 1 }}
      whileHover={{ y: -3 }}
      whileTap={{ scale: 0.96 }}
      transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1], delay: index * 0.06 }}
      className={`group relative aspect-square w-full rounded-full ${tint} text-ink flex flex-col items-center justify-center text-center px-2 ${canExpand ? 'cursor-pointer' : 'cursor-default'}`}
    >
      <span className="absolute inset-0 rounded-full ring-1 ring-ink/10 group-hover:ring-ink/25 transition-all duration-300" />
      <span className="opacity-70 mb-1"><AnimatedIcon motion={motionKind}>{icon}</AnimatedIcon></span>
      <span className="font-display text-2xl sm:text-[28px] font-light tracking-[-0.05em] leading-none tabular-nums">{value}</span>
      <span className="mt-1.5 font-mono text-[9px] uppercase tracking-[0.16em] text-ink/70 line-clamp-1">{label}</span>
      <span className="mt-0.5 text-[10px] text-ink/50 line-clamp-1">{subtitle}</span>
    </motion.button>
    {showTooltip && canExpand && typeof document !== 'undefined' && createPortal(
      <div className="fixed inset-0 z-[9999999] flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm" onClick={(e) => { e.stopPropagation(); setShowTooltip(false); }}>
        <motion.div
          initial={{ opacity: 0, scale: 0.92, y: 10 }} animate={{ opacity: 1, scale: 1, y: 0 }}
          transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
          className="bg-surface text-ink border border-black/[0.06] dark:border-white/[0.06] p-7 rounded-[28px] max-w-sm w-full"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="flex justify-between items-start mb-5">
            <div className={`w-12 h-12 rounded-full ${tint} flex items-center justify-center`}><AnimatedIcon motion={motionKind} active>{icon}</AnimatedIcon></div>
            <button onClick={() => setShowTooltip(false)} className="w-8 h-8 rounded-full flex items-center justify-center text-muted hover:text-ink hover:bg-ink/5"><X size={15}/></button>
          </div>
          <p className="font-mono text-[10px] uppercase tracking-[0.16em] text-muted">{tooltipTitle || label}</p>
          <p className="font-display text-5xl font-light tracking-[-0.05em] leading-none mt-2 tabular-nums">{value}</p>
          {tooltip && <p className="text-sm text-ink/70 leading-relaxed mt-4">{tooltip}</p>}
          {extraInfo && <div className="mt-4 pt-4 border-t border-ink/10 text-xs font-medium text-ok">{extraInfo}</div>}
        </motion.div>
      </div>,
      document.body
    )}
  </>
  );
};

const TRANSLATING_LABELS: Record<string, string> = {
  pt: 'Traduzindo...',
  en: 'Translating...',
  es: 'Traduciendo...',
  fr: 'Traduction...',
  de: 'Wird übersetzt...',
  it: 'Traducendo...',
  ru: 'Перевод...',
  zh: '正在翻译...',
  ja: '翻訳中...',
  nl: 'Vertalen...'
};

interface FlashcardGridProps {
  flashcards: Flashcard[];
  onDelete: (id: string) => void;
  audioLoadingId: string | null;
  setAudioLoadingId: (id: string | null) => void;
  targetLanguage: string;
  currentDeckLanguage?: string;
  onRefresh: () => void;
  onDeckCompleted?: () => void;
  onCardReviewed?: (id: string, newHistory: string, nextReviewDate?: string) => void;
  isWordDeck?: boolean;
}

const formatIntervalLabel = (interval: number): string => {
  if (interval === 0) return '< 10 min';
  if (interval === 1) return '1 dia';
  if (interval < 30) return `${interval} dias`;
  const months = Math.floor(interval / 30);
  if (months === 1) return '1 mês';
  return `${months} meses`;
};

const FlashcardGrid: React.FC<FlashcardGridProps> = ({ 
  flashcards, onDelete, audioLoadingId, setAudioLoadingId, 
  targetLanguage, currentDeckLanguage, onRefresh, 
  onDeckCompleted, onCardReviewed, isWordDeck
}) => {
  const { t, i18n } = useTranslation();
  const safeT = (key: string, fallback: string) => {
    const res = t(key);
    return res === key ? fallback : res;
  };
  const { user } = useAuth();
  const { refreshProfile, profile } = useData() as any;

  const translatingLabel = TRANSLATING_LABELS[(i18n.language || 'pt').split('-')[0].toLowerCase()] || TRANSLATING_LABELS.pt;

  const [selectedWordInfo, setSelectedWordInfo] = useState<{
    word: string;
    translation: string;
    loading: boolean;
    x: number;
    y: number;
  } | null>(null);

  useEffect(() => {
    const handleGlobalClick = () => {
      setSelectedWordInfo(null);
    };
    window.addEventListener('click', handleGlobalClick);
    return () => {
      window.removeEventListener('click', handleGlobalClick);
    };
  }, []);

  const unifiedDict = useUnifiedDictionary();

  const handleWordClick = async (e: React.MouseEvent<HTMLSpanElement>, word: string, isSwapped: boolean = false) => {
    e.stopPropagation();
    const cleanWord = word.replace(/[.,\/#!$%\^&\*;:{}=\-_`~()?\"']/g, "").trim();
    if (!cleanWord) return;

    const userUiLang = (i18n.language || 'pt').split('-')[0].toLowerCase();
    const cardDeckLang = (currentDeckLanguage || (localQueue[0] && (localQueue[0].lang_code || 'en')) || 'en').split('-')[0].toLowerCase();
    
    const sourceLangCode = isSwapped ? userUiLang : cardDeckLang;
    const targetLangCode = isSwapped ? cardDeckLang : userUiLang;

    unifiedDict.handleWordClick(e, cleanWord, sourceLangCode, targetLangCode);
  };

  const renderClickableText = (text: string, isSwapped: boolean = false) => {
    const parts = text.split(/(\s+)/);
    return parts.map((part, idx) => {
      const hasLetters = /[a-zA-ZÀ-ÿ\w]/.test(part);
      if (hasLetters) {
        return (
          <span 
            key={idx}
            onClick={(e) => handleWordClick(e, part, isSwapped)}
            className="hover:text-primary hover:underline cursor-pointer transition-colors relative inline-block text-xl sm:text-3xl font-medium dark:text-ink"
          >
            {part}
          </span>
        );
      }
      return <span key={idx} className="text-xl sm:text-3xl font-medium text-ink dark:text-slate-200">{part}</span>;
    });
  };

  const [flippedIds, setFlippedIds] = useState<Set<string>>(new Set());
  const [dismissedIds, setDismissedIds] = useState<Set<string>>(new Set());
  const [localQueue, setLocalQueue] = useState<Flashcard[]>([]);
  const [pendingSrsCards, setPendingSrsCards] = useState<Flashcard[]>(() => {
    try {
      if (typeof localStorage !== 'undefined') {
        const storedKey = `libritui_pending_srs_${user?.id || 'guest'}`;
        const saved = localStorage.getItem(storedKey);
        if (saved) {
          const parsed = JSON.parse(saved);
          if (Array.isArray(parsed)) return parsed;
        }
      }
    } catch (e) {}
    return [];
  });
  const validCardIds = useMemo(() => {
    return new Set((flashcards || []).map(f => String(f.id)));
  }, [flashcards]);

  const deckPendingSrsCards = useMemo(() => {
    return pendingSrsCards.filter(c => validCardIds.has(String(c.id)));
  }, [pendingSrsCards, validCardIds]);

  const [nowTimestamp, setNowTimestamp] = useState<number>(Date.now());
  const [showFullText, setShowFullText] = useState<{ id: string, text: string, type: 'original' | 'translation' | 'user_input' } | null>(null);
  const audioRef = useRef<HTMLAudioElement | null>(null);

  const [cardInputs, setCardInputs] = useState<Record<string, string>>({});
  const [submittedInputs, setSubmittedInputs] = useState<Record<string, string>>({});
  const [isDeckSwapped, setIsDeckSwapped] = useState<boolean>(() => {
    try {
      return localStorage.getItem('libritui_flashcard_swap_mode') === 'true';
    } catch {
      return false;
    }
  });

  const toggleSwapDeck = () => {
    setIsDeckSwapped(prev => {
      const next = !prev;
      try {
        localStorage.setItem('libritui_flashcard_swap_mode', String(next));
      } catch (e) {}
      return next;
    });
  };

  const handleUserSubmitTranslation = (cardIdStr: string, card: Flashcard) => {
    const currentInput = cardInputs[cardIdStr] || '';
    if (!currentInput.trim()) {
      toggleFlip(cardIdStr);
      return;
    }
    setSubmittedInputs(prev => ({ ...prev, [cardIdStr]: currentInput.trim() }));
    setSelectedWordInfo(null);
    setFlippedIds(prev => {
      const next = new Set(prev);
      next.add(cardIdStr);
      return next;
    });
  };

  const renderComparativeTranslation = (userTyped: string, targetTranslation: string, isTextTranslation: boolean = false) => {
    if (!targetTranslation) return null;

    const targetTokens = targetTranslation.split(/(\s+)/);

    // Normalize curly quotes/backticks to standard single quotes
    const normalizedUser = (userTyped || '').replace(/[’‘`]/g, "'").toLowerCase();
    
    // Extract user words, removing leading/trailing punctuation while keeping internal apostrophes (e.g. "J'ai")
    const userWordsClean = normalizedUser
      .split(/\s+/)
      .map(w => w.replace(/^[^\wÀ-ÿ']+|[^\wÀ-ÿ']+$/g, ""))
      .filter(Boolean);

    const userWordsStripped = userWordsClean.map(w => w.replace(/'/g, ""));

    interface TargetWordInfo {
      tokenIndex: number;
      withApostrophe: string;
      stripped: string;
    }

    const targetWordsInfo: TargetWordInfo[] = [];
    targetTokens.forEach((token, idx) => {
      const isWord = /[a-zA-ZÀ-ÿ\w]/.test(token);
      if (isWord) {
        const normToken = token.replace(/[’‘`]/g, "'").toLowerCase();
        const withApostrophe = normToken.replace(/^[^\wÀ-ÿ']+|[^\wÀ-ÿ']+$/g, "");
        const stripped = withApostrophe.replace(/'/g, "");
        targetWordsInfo.push({
          tokenIndex: idx,
          withApostrophe,
          stripped
        });
      }
    });

    const M = targetWordsInfo.length;
    const N = userWordsClean.length;

    // Sequence alignment (DP) to find Longest Common Subsequence of matched words in order
    const dp: number[][] = Array.from({ length: M + 1 }, () => Array(N + 1).fill(0));
    type ChoiceType = 'none' | 'skip_t' | 'skip_u' | 'match_1_1' | 'match_1_2' | 'match_2_1';
    const choice: ChoiceType[][] = Array.from({ length: M + 1 }, () => Array(N + 1).fill('none'));

    for (let i = 1; i <= M; i++) {
      for (let j = 1; j <= N; j++) {
        // Default transition: skip user word j-1 or skip target word i-1
        if (dp[i][j - 1] >= dp[i - 1][j]) {
          dp[i][j] = dp[i][j - 1];
          choice[i][j] = 'skip_u';
        } else {
          dp[i][j] = dp[i - 1][j];
          choice[i][j] = 'skip_t';
        }

        const tCurr = targetWordsInfo[i - 1];
        const uCurrClean = userWordsClean[j - 1];
        const uCurrStripped = userWordsStripped[j - 1];

        // 1. Single target word <-> single user word match
        const match11 = (uCurrClean === tCurr.withApostrophe) ||
                        (uCurrStripped && uCurrStripped === tCurr.stripped);

        if (match11) {
          const val = dp[i - 1][j - 1] + 1;
          if (val > dp[i][j]) {
            dp[i][j] = val;
            choice[i][j] = 'match_1_1';
          }
        }

        // 2. Single target word <-> 2 user words (e.g. target "j'ai", user "j" "ai")
        if (j >= 2) {
          const uPrevStripped = userWordsStripped[j - 2];
          const combinedUser = uPrevStripped + uCurrStripped;
          if (combinedUser === tCurr.stripped) {
            const val = dp[i - 1][j - 2] + 1;
            if (val > dp[i][j]) {
              dp[i][j] = val;
              choice[i][j] = 'match_1_2';
            }
          }
        }

        // 3. 2 target words <-> single user word (e.g. target "j" "ai", user "j'ai")
        if (i >= 2) {
          const tPrev = targetWordsInfo[i - 2];
          const combinedTarget = tPrev.stripped + tCurr.stripped;
          if (uCurrStripped === combinedTarget) {
            const val = dp[i - 2][j - 1] + 2;
            if (val > dp[i][j]) {
              dp[i][j] = val;
              choice[i][j] = 'match_2_1';
            }
          }
        }
      }
    }

    // Backtrack to find exact matched target token indices
    const matchedTargetTokenIndices = new Set<number>();
    let currI = M;
    let currJ = N;

    while (currI > 0 && currJ > 0) {
      const ch = choice[currI][currJ];
      if (ch === 'match_1_1') {
        matchedTargetTokenIndices.add(targetWordsInfo[currI - 1].tokenIndex);
        currI -= 1;
        currJ -= 1;
      } else if (ch === 'match_1_2') {
        matchedTargetTokenIndices.add(targetWordsInfo[currI - 1].tokenIndex);
        currI -= 1;
        currJ -= 2;
      } else if (ch === 'match_2_1') {
        matchedTargetTokenIndices.add(targetWordsInfo[currI - 1].tokenIndex);
        matchedTargetTokenIndices.add(targetWordsInfo[currI - 2].tokenIndex);
        currI -= 2;
        currJ -= 1;
      } else if (ch === 'skip_u') {
        currJ -= 1;
      } else {
        currI -= 1;
      }
    }

    return (
      <div className="w-full flex flex-col items-center gap-3 my-auto">
        <div className="text-[10px] font-normal uppercase font-mono text-slate-400 tracking-[0.14em]">
          Sua Tradução vs Correção:
        </div>
        <div className="flex flex-wrap justify-center items-center gap-1.5 max-w-full leading-relaxed">
          {targetTokens.map((token, idx) => {
            const isWord = /[a-zA-ZÀ-ÿ\w]/.test(token);
            if (!isWord) {
              return <span key={idx} className="text-lg sm:text-2xl font-medium text-slate-400 dark:text-slate-500">{token}</span>;
            }

            const isMatch = matchedTargetTokenIndices.has(idx);

            return (
              <span
                key={idx}
                onClick={(e) => handleWordClick(e, token, isTextTranslation)}
                className={`px-2 py-0.5 rounded-lg text-lg sm:text-2xl font-medium transition-all cursor-pointer hover:scale-105 ${
                  isMatch
                    ? 'bg-ok/20 dark:bg-ok/30 text-ok dark:text-ok border border-ok/40 hover:bg-ok/40'
                    : 'bg-bad/20 dark:bg-bad/30 text-bad dark:text-bad border border-bad/40 hover:bg-bad/40'
                }`}
                title="Clique para traduzir palavra"
              >
                {token}
              </span>
            );
          })}
        </div>

        {/* Display user typed response */}
        {userTyped && (
          <div className="mt-2 w-full flex flex-col items-center gap-1">
            <span className="text-[10px] font-normal uppercase font-mono text-slate-400 tracking-[0.14em]">
              Sua Resposta:
            </span>
            <div className="flex items-center justify-center gap-2 max-w-full px-4">
              <p className="text-xs sm:text-sm font-bold text-slate-600 dark:text-slate-300 italic truncate max-w-[240px] sm:max-w-[340px] text-center">
                "{userTyped}"
              </p>
              {userTyped.length > 35 && (
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    setShowFullText({
                      id: 'user_input',
                      text: userTyped,
                      type: 'user_input'
                    });
                  }}
                  className="p-1 bg-slate-200 dark:bg-slate-800 text-slate-500 hover:text-primary rounded-lg transition-all text-xs font-bold shrink-0 cursor-pointer border border-slate-300/60 dark:border-slate-700/60 hover:scale-105 active:scale-95"
                  title="Ver resposta completa"
                >
                  <Plus size={14} />
                </button>
              )}
            </div>
          </div>
        )}
      </div>
    );
  };

  useEffect(() => {
    return () => {
      try { window.speechSynthesis.cancel(); } catch (e) {}
      try { textToSpeechService.stop(); } catch (e) {}
      if (audioRef.current) {
        try { audioRef.current.pause(); } catch (e) {}
        audioRef.current = null;
      }
    };
  }, []);

  const deckKey = useMemo(() => {
    return (flashcards || []).map(f => String(f.id)).sort().join(',');
  }, [flashcards]);

  const prevDeckKeyRef = useRef<string>(deckKey);

  useEffect(() => {
    if (prevDeckKeyRef.current !== deckKey || localQueue.length === 0) {
      prevDeckKeyRef.current = deckKey;
      if (flashcards.length > 0) {
        const today = getLocalISOString().split('T')[0];
        
        // Embaralha antes da ordenação (que é estável): cards da mesma data saem em ordem aleatória
        const shuffled = [...flashcards];
        for (let i = shuffled.length - 1; i > 0; i--) {
          const j = Math.floor(Math.random() * (i + 1));
          [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
        }

        // Separate due and concluded cards using next_review_date (String comparison)
        const dueCards = shuffled.filter(f => !f.next_review_date || f.next_review_date <= today);
        const concludedCards = shuffled.filter(f => f.next_review_date && f.next_review_date > today);

        // Sort both groups by next_review_date to respect SRS order
        const sortFn = (a: Flashcard, b: Flashcard) => {
          const dateA = a.next_review_date || '0000-00-00';
          const dateB = b.next_review_date || '0000-00-00';
          return dateA.localeCompare(dateB);
        };

        dueCards.sort(sortFn);
        concludedCards.sort(sortFn);

        // Final Queue: Due cards first, then concluded cards
        const finalQueue = [...dueCards, ...concludedCards];

        setLocalQueue(finalQueue);
        setDismissedIds(new Set());
        setFlippedIds(new Set());
        setCardInputs({});
        setSubmittedInputs({});
      }
    }
  }, [flashcards, deckKey, localQueue.length]);

  // Real-time SRS timer tick every 1s: checks if any pending SRS card is due (filtered for THIS active deck)
  useEffect(() => {
    const timer = setInterval(() => {
      const now = Date.now();
      setNowTimestamp(now);

      if (pendingSrsCards.length > 0) {
        const dueCards = pendingSrsCards.filter(c => 
          c.reviewDueAtTimestamp && 
          c.reviewDueAtTimestamp <= now &&
          validCardIds.has(String(c.id))
        );
        if (dueCards.length > 0) {
          const dueIds = new Set(dueCards.map(c => String(c.id)));
          setPendingSrsCards(prev => {
            const nextPending = prev.filter(c => !dueIds.has(String(c.id)));
            try {
              const storageKey = `libritui_pending_srs_${user?.id || 'guest'}`;
              localStorage.setItem(storageKey, JSON.stringify(nextPending));
            } catch (e) {}
            return nextPending;
          });

          setLocalQueue(prev => {
            if (prev.length === 0) return dueCards;
            const [activeCard, ...rest] = prev;
            const restFiltered = rest.filter(c => !dueIds.has(String(c.id)));
            return [activeCard, ...dueCards, ...restFiltered];
          });
        }
      }
    }, 1000);

    return () => clearInterval(timer);
  }, [pendingSrsCards, user?.id, validCardIds]);

  const toggleFlip = (id: string) => {
    stopAudio();
    setSelectedWordInfo(null);
    setFlippedIds(prev => {
      const next = new Set(prev);
      if (next.has(id.toString())) next.delete(id.toString()); else next.add(id.toString());
      return next;
    });
  };

  const [isProcessingFeedback, setIsProcessingFeedback] = useState(false);
  const [cardHistoryStack, setCardHistoryStack] = useState<Array<{
    card: Flashcard;
    flipped: boolean;
    userInput?: string;
    submittedInput?: string;
  }>>([]);

  const handleUndoLastCard = (e: React.MouseEvent) => {
    e.stopPropagation();
    stopAudio();
    if (cardHistoryStack.length === 0) return;
    const lastItem = cardHistoryStack[cardHistoryStack.length - 1];
    setCardHistoryStack(prev => prev.slice(0, -1));

    setLocalQueue(prev => {
      const cleanQueue = prev.filter(c => String(c.id) !== String(lastItem.card.id));
      return [lastItem.card, ...cleanQueue];
    });

    const cardIdStr = String(lastItem.card.id);
    if (lastItem.flipped) {
      setFlippedIds(prev => new Set([...prev, cardIdStr]));
    } else {
      setFlippedIds(prev => {
        const next = new Set(prev);
        next.delete(cardIdStr);
        return next;
      });
    }

    if (lastItem.userInput !== undefined) {
      setCardInputs(prev => ({ ...prev, [cardIdStr]: lastItem.userInput || '' }));
    }
    if (lastItem.submittedInput !== undefined) {
      setSubmittedInputs(prev => ({ ...prev, [cardIdStr]: lastItem.submittedInput || '' }));
    }
  };

  const handleSrsFeedback = (e: React.MouseEvent, cardId: string, quality: 'again' | 'hard' | 'good' | 'easy') => {
    e.stopPropagation();
    stopAudio();
    setSelectedWordInfo(null);
    if (isProcessingFeedback) return;
    setIsProcessingFeedback(true);

    const snapshotCard = localQueue[0] ? JSON.parse(JSON.stringify(localQueue[0])) : null;

    if (snapshotCard) {
      const snapshotIdStr = String(snapshotCard.id);
      setCardHistoryStack(prev => {
        const next = [...prev, {
          card: snapshotCard,
          flipped: flippedIds.has(snapshotIdStr),
          userInput: cardInputs[snapshotIdStr],
          submittedInput: submittedInputs[snapshotIdStr]
        }];
        return next.slice(-5);
      });
    }

    const cardLang = (
      snapshotCard?.lang_code || 
      snapshotCard?.target_lang || 
      snapshotCard?.idioma_alvo || 
      activeStudyTracker.getCurrentLang() ||
      profile?.learning_language || 
      (typeof localStorage !== 'undefined' ? localStorage.getItem('libritui_learning_lang') || localStorage.getItem('current_language') : '') ||
      ''
    ).toString().trim().split('-')[0].split('_')[0].toLowerCase();

    const safeCardLang = (cardLang && cardLang !== 'all' && cardLang.length >= 2) ? cardLang : (activeStudyTracker.getCurrentLang() || null);

    const effectiveCardId = (cardId || snapshotCard?.id || snapshotCard?._id || snapshotCard?.card_id || '').toString();
    const effectiveUserId = user?.id || (typeof localStorage !== 'undefined' ? localStorage.getItem('libritui_user_id') || undefined : undefined);

    // Clear typed text input & submitted comparative translation for this card so next review is clean
    setCardInputs(prev => {
      const next = { ...prev };
      delete next[effectiveCardId];
      if (cardId) delete next[cardId.toString()];
      return next;
    });
    setSubmittedInputs(prev => {
      const next = { ...prev };
      delete next[effectiveCardId];
      if (cardId) delete next[cardId.toString()];
      return next;
    });

    // Background Non-Blocking Telemetry (Never delays UI or blocks card transition)
    setTimeout(() => {
      try {
        const isCorrect = quality === 'good' || quality === 'easy';
        soundService.play(isCorrect ? 'success' : 'error');
        
        if (safeCardLang && safeCardLang !== 'all') {
          activeStudyTracker.setUserContext(effectiveUserId, safeCardLang);
          activeStudyTracker.logExercisePrecision(isCorrect, effectiveUserId, safeCardLang);
          activeStudyTracker.logConsistencySession(effectiveUserId, safeCardLang);
        }

        if ((quality === 'again' || quality === 'hard') && effectiveUserId && effectiveCardId) {
          const errorLang = safeCardLang || activeStudyTracker.getCurrentLang();
          if (errorLang && errorLang !== 'all') {
            import('../services/errorReviewService').then(({ errorReviewService }) => {
              errorReviewService.recordError({
                userId: effectiveUserId,
                language: errorLang,
                exerciseType: 'flashcards',
                sourceOrigin: 'direct_url',
                itemId: effectiveCardId,
                itemPayload: snapshotCard || { cardId: effectiveCardId },
                userInput: quality
              }).catch(() => {});
            }).catch(() => {});
          }
        }
      } catch (err) {
        console.warn('[FlashcardsPage] Background telemetry issue:', err);
      }
    }, 0);
    
    // Centralized Mapping Rule: 1=Again, 2=Hard, 3=Good, 4=Easy
    const qualityMap: Record<string, string> = { 
      again: '1', 
      hard: '2', 
      good: '3', 
      easy: '4' 
    };
    const today = getLocalISOString().split('T')[0];
    const qualityId = qualityMap[quality];
    const newEntry = `${qualityId}:${today}`;

    let historyArray = currentCard.history ? currentCard.history.split(',') : [];
    historyArray.push(newEntry);
    if (historyArray.length > 5) historyArray = historyArray.slice(-5);
    const updatedHistoryString = historyArray.join(',');
    
    currentCard.history = updatedHistoryString;

    // Calculate next SRS state immediately for the sync queue
    const srsState = flashcardManager.calculateNextSrsState(currentCard, quality);

    if (quality === 'good' || quality === 'easy') {
      // Remove card from pending SRS queue if present
      setPendingSrsCards(prev => {
        const nextPending = prev.filter(c => String(c.id) !== String(cardId));
        try {
          const storageKey = `libritui_pending_srs_${user?.id || 'guest'}`;
          localStorage.setItem(storageKey, JSON.stringify(nextPending));
        } catch (e) {}
        return nextPending;
      });

      // Bug Correction: We MUST show the updated square before removing the card
      const updatedQueue = [currentCard, ...localQueue.slice(1)];
      setLocalQueue(updatedQueue);
      
      setTimeout(() => {
        setLocalQueue(prev => prev.slice(1));
        // Clear flip state
        setFlippedIds(prev => {
          const next = new Set(prev);
          next.delete(cardId);
          return next;
        });
        // Track dismissed count for session progress
        setDismissedIds(prev => new Set([...prev, cardId]));
        setIsProcessingFeedback(false);
      }, 250);
    } else {
      // For 'Again' (1 min) or 'Hard' (10 min), schedule into pendingSrsCards with timestamp
      const intervalMs = quality === 'again' ? 60000 : 600000;
      const reviewDueAtTimestamp = Date.now() + intervalMs;
      const scheduledCard: Flashcard = {
        ...currentCard,
        history: updatedHistoryString,
        reviewDueAtTimestamp
      };

      setPendingSrsCards(prev => {
        const filtered = prev.filter(c => String(c.id) !== String(currentCard.id));
        const updated = [...filtered, scheduledCard];
        try {
          const storageKey = `libritui_pending_srs_${user?.id || 'guest'}`;
          localStorage.setItem(storageKey, JSON.stringify(updated));
        } catch (e) {}
        return updated;
      });

      // Advance queue to next card
      setLocalQueue(prev => prev.slice(1));
      setFlippedIds(prev => {
        const next = new Set(prev);
        next.delete(cardId);
        return next;
      });
      setTimeout(() => setIsProcessingFeedback(false), 50); // slight delay to prevent double tapping
    }
    
    // --- 2. PARENT STATE SYNC ---
    // Propagate the updated history to the parent state immediately
    // This prevents stale data if the queue re-initializes from the 'flashcards' prop
    onCardReviewed?.(cardId, updatedHistoryString, srsState.nextReviewDate);

    // --- 3. BACKGROUND PERSISTENCE ---
    // Push the update to the background queue, which limits DB writes to 1 per second
    // Pass the pre-calculated history and next review date to ensure D1/IndexedDB matches the UI exactly
    flashcardManager.enqueueSrsUpdate(currentCard, quality, currentCard.history, srsState.nextReviewDate);

    // Track unique words learned from flashcards (works for standard & custom book flashcards)
    if (user?.id) {
      uniqueWordsService.recordExerciseCompletion(
        user.id,
        currentCard.lang_code || targetLanguage || 'en',
        'flashcards',
        currentCard.id,
        currentCard.original || currentCard.word || ''
      );

      // Record exercise progress for study plan & tracking
      const isPhraseDeck = currentCard.category?.toLowerCase().includes('frase') || 
                           (currentCard.original && currentCard.original.trim().includes(' '));
      const resolvedExerciseType = isPhraseDeck ? 'flashcards_phrases' : 'flashcards_words';

      exerciseProgressService.recordPractice({
        userId: user.id,
        exerciseType: resolvedExerciseType,
        langCode: currentCard.lang_code || targetLanguage || 'en',
        level: currentCard.category?.replace(/\D+/g, '') || 'A1',
        itemId: String(currentCard.id),
        score: quality === 'easy' ? 100 : quality === 'good' ? 85 : quality === 'hard' ? 60 : 40,
        isSuccess: quality !== 'again'
      }).catch(err => console.error('[FlashcardsPage] Progress record error:', err));
    }
  };

  // We no longer filter by dismissedIds because user wants them at the end of the deck
  // visibleCards will just be the full localQueue reordered
  const visibleCards = localQueue;
  const currentCard = visibleCards[0];

  const stopAudio = useCallback(() => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current = null;
    }
    if (typeof window !== 'undefined' && window.speechSynthesis) {
      window.speechSynthesis.cancel();
    }
    if (textToSpeechService && typeof (textToSpeechService as any).stop === 'function') {
      (textToSpeechService as any).stop();
    }
    setAudioLoadingId(null);
  }, []);

  const handleAudio = async (e: React.MouseEvent, card: Flashcard, isOriginal: boolean) => {
    e.stopPropagation();
    const text = isOriginal ? card.original : card.translation;
    if (!text) return;
    const cardIdStr = card.id.toString();

    // If audio is currently playing for this card, stop it immediately!
    if (audioLoadingId === cardIdStr) {
      stopAudio();
      return;
    }

    stopAudio();
    setAudioLoadingId(cardIdStr);
    try {
      const cardLang = currentDeckLanguage || card.lang_code || 'en';
      const locale = isOriginal ? (ISO_TO_LOCALE[cardLang] || 'en-US') : targetLanguage;
      await textToSpeechService.speak(text, locale, 0.75, card.book_id || undefined);
      setAudioLoadingId(null);
    } catch (err) {
      console.warn("Audio Playback Issue:", err);
      setAudioLoadingId(null);
    }
  };

  // Sync feedback history from IndexedDB for localQueue cards
  useEffect(() => {
    if (!user || user.id === 'guest') return;
    let isSubscribed = true;

    localFlashcardDb.getAllFeedback().then(feedbackMap => {
      if (!isSubscribed || !feedbackMap) return;
      setLocalQueue(prev => {
        let changed = false;
        const nextQueue = prev.map(card => {
          const f = feedbackMap[String(card.id)];
          if (f && f.history && f.history !== card.history) {
            changed = true;
            return { ...card, history: f.history };
          }
          return card;
        });
        return changed ? nextQueue : prev;
      });
    }).catch(e => console.warn('[FlashcardGrid] History sync error:', e));

    return () => { isSubscribed = false; };
  }, [localQueue.length, user]);

  // Real-time cross-device sync: fetch D1 feedback on tab focus/visibilitychange
  useEffect(() => {
    if (!user || user.id === 'guest') return;

    let lastSync = 0;
    const syncFromD1 = async () => {
      const now = Date.now();
      if (now - lastSync < 4000) return; // Throttled to 4s
      lastSync = now;

      try {
        const feedbackMap = await flashcardManager.syncFeedbackFromD1();
        if (feedbackMap && Object.keys(feedbackMap).length > 0) {
          setLocalQueue(prev => prev.map(card => {
            const f = feedbackMap[String(card.id)];
            if (f && f.history && f.history !== card.history) {
              return { ...card, history: f.history };
            }
            return card;
          }));
        }
      } catch (err) {
        console.warn('[FlashcardGrid] Window focus sync failed:', err);
      }
    };

    const handleVisibility = () => {
      if (document.visibilityState === 'visible') {
        syncFromD1();
      }
    };

    window.addEventListener('focus', syncFromD1);
    document.addEventListener('visibilitychange', handleVisibility);

    return () => {
      window.removeEventListener('focus', syncFromD1);
      document.removeEventListener('visibilitychange', handleVisibility);
    };
  }, [user]);

  if (!Array.isArray(flashcards) || flashcards.length === 0) return (
    <div className="border border-black/[0.06] dark:border-white/[0.06] rounded-[28px] p-12 sm:p-20 text-center flex flex-col items-center justify-center space-y-4 bg-slate-50/50 dark:bg-slate-900/30 backdrop-blur-sm animate-in fade-in duration-300">
      <div className="w-16 h-16 rounded-3xl bg-primary/10 text-primary flex items-center justify-center animate-pulse">
        <Loader2 size={36} className="animate-spin text-primary" />
      </div>
      <h3 className="text-lg font-medium text-ink dark:text-slate-200 tracking-[-0.03em]">
        Sincronizando Baralho de Exercícios...
      </h3>
      <p className="text-xs text-slate-400 font-medium max-w-sm">
        Carregando os flashcards e preparando os itens para o seu roteiro de estudos.
      </p>
    </div>
  );

  return (
    <div className="relative w-full max-w-xl mx-auto h-[540px] sm:h-[580px] mt-8 sm:mt-12 mb-24 flex flex-col items-center px-4 sm:px-0">
      {showFullText && typeof document !== 'undefined' && createPortal(
        <div className="fixed inset-0 z-[9999999] flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm" onClick={(e) => { e.stopPropagation(); setShowFullText(null); }}>
          <div className="bg-slate-900 border border-slate-700 p-6 rounded-[24px] w-full max-w-lg max-h-[80vh] flex flex-col animate-in zoom-in-95 duration-200" onClick={(e) => e.stopPropagation()}>
            <div className="flex justify-between items-start mb-4 shrink-0">
              <span className="text-[10px] font-normal uppercase font-mono tracking-[0.14em] text-primary/40">{showFullText.type === 'original' ? safeT('flashcards.original', 'Original') : showFullText.type === 'user_input' ? 'Sua Resposta' : safeT('flashcards.translation', 'Tradução')}</span>
              <button onClick={(e) => { e.stopPropagation(); setShowFullText(null); }} className="text-slate-400 hover:text-white bg-slate-800 p-2 rounded-full cursor-pointer"><X size={16}/></button>
            </div>
            <div className="overflow-y-auto custom-scrollbar flex-1 pr-2">
               <p className="text-white text-lg md:text-xl font-bold leading-relaxed whitespace-pre-wrap">{showFullText.text}</p>
            </div>
          </div>
        </div>,
        document.body
      )}
      <AnimatePresence mode="popLayout">
        {visibleCards.length > 0 ? (
          visibleCards.slice(0, 3).reverse().map((card, index) => {
            const stackTotal = Math.min(visibleCards.length, 3);
            const isTop = index === stackTotal - 1;
            const cardIdStr = card.id.toString();
            const isFlipped = flippedIds.has(cardIdStr);
            const isLoading = audioLoadingId === cardIdStr;
            const isCardSwapped = isDeckSwapped;

            const frontText = isCardSwapped ? (card.translation || '') : (card.original || '');
            const backText = isCardSwapped ? (card.original || '') : (card.translation || '');

            const frontLabel = isCardSwapped ? safeT('flashcards.translation', 'Tradução') : safeT('flashcards.original', 'Original');
            const backLabel = isCardSwapped ? safeT('flashcards.original', 'Original') : safeT('flashcards.translation', 'Tradução');
            
            // stackIndex 0 is the top card
            const stackIndex = stackTotal - index - 1;

            return (
              <motion.div
                key={card.id}
                initial={{ scale: 0.95, opacity: 0, y: 10 * stackIndex }}
                animate={{ 
                  scale: 1 - (stackIndex * 0.05), 
                  opacity: 1,
                  y: 10 * stackIndex,
                  filter: stackIndex > 0 ? `blur(${stackIndex * 1}px)` : 'none'
                }}
                exit={{ 
                  x: 300, 
                  opacity: 0, 
                  rotate: 20,
                  scale: 0.9,
                  transition: { duration: 0.25, ease: "circIn" }
                }}
                className="absolute inset-0"
                style={{ zIndex: 100 - stackIndex }}
              >
                <div 
                  onClick={(e) => {
                    e.stopPropagation();
                    if (isTop) toggleFlip(cardIdStr);
                  }}
                  style={{ 
                    cursor: isTop ? 'pointer' : 'default',
                    pointerEvents: isTop ? 'auto' : 'none',
                    position: 'relative',
                  }}
                  className="relative w-full h-[480px] sm:h-[520px] bg-surface rounded-[28px] flex flex-col justify-between"
                >
                  {/* Front Side — fades out when flipped */}
                  <div 
                    className="absolute inset-0 bg-surface rounded-[28px] p-6 sm:p-10 border border-black/[0.06] dark:border-white/[0.06] shadow-[0_24px_60px_-24px_rgba(0,0,0,0.35)] flex flex-col justify-between text-center overflow-hidden" 
                    style={{ 
                      opacity: isFlipped ? 0 : 1,
                      transition: 'opacity 0s',
                      pointerEvents: isFlipped ? 'none' : 'auto',
                    }}
                  >
                    <div className="flex-1 flex flex-col justify-center items-center overflow-y-auto custom-scrollbar px-2 mt-4 min-h-0 w-full">
                      <span className="text-[10px] sm:text-xs font-normal uppercase font-mono text-primary/40 dark:text-slate-400 mb-4 sm:mb-6 tracking-[0.14em] shrink-0">{frontLabel}</span>
                      <p className="text-xl sm:text-3xl font-medium leading-tight dark:text-ink break-words w-full">{renderClickableText(frontText, isCardSwapped)}</p>
                    </div>
                    
                    {isTop && !isFlipped && (
                      <div className="w-full flex flex-col items-center mt-2 sm:mt-4 shrink-0 space-y-3 z-20">
                        <div 
                          onClick={(e) => e.stopPropagation()} 
                          className="w-full flex items-center gap-2 px-4 py-2.5 bg-slate-100 dark:bg-slate-800/80 rounded-2xl border border-slate-200/50 dark:border-slate-700/50"
                        >
                          <input
                            type="text"
                            value={cardInputs[cardIdStr] || ''}
                            onChange={(e) => setCardInputs(prev => ({ ...prev, [cardIdStr]: e.target.value }))}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') {
                                e.stopPropagation();
                                handleUserSubmitTranslation(cardIdStr, card);
                              }
                            }}
                            placeholder={i18nInstance.t('ui.fc.typeTranslation')}
                            className="flex-1 bg-transparent border-none outline-none text-xs sm:text-sm font-bold text-ink placeholder:text-slate-400"
                          />
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleUserSubmitTranslation(cardIdStr, card);
                            }}
                            className="w-9 h-9 rounded-full bg-ok/20 hover:bg-ok/30 flex items-center justify-center transition-all shrink-0 cursor-pointer border border-ok/30"
                            title={i18nInstance.t('ui.fc.sendTranslation')}
                          >
                            <Send size={16} className="text-white fill-white" />
                          </button>
                        </div>

                        <div 
                          onClick={(e) => {
                            e.stopPropagation();
                            toggleFlip(cardIdStr);
                          }}
                          className="opacity-40 hover:opacity-80 flex flex-col items-center cursor-pointer transition-opacity"
                        >
                          <p className="text-[10px] font-normal uppercase tracking-[0.14em] font-mono mb-1">{safeT('flashcards.clickToViewTranslation', 'Clique para ver tradução')}</p>
                          <ChevronRight className="rotate-90" size={14} />
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Back Side — fades in when flipped */}
                  <div className="absolute inset-0 bg-slate-50 dark:bg-slate-950 rounded-[28px] p-6 sm:p-10 border border-primary/20 shadow-[0_24px_60px_-24px_rgba(0,0,0,0.35)] flex flex-col items-center justify-between text-center overflow-hidden"
                    style={{ 
                      opacity: isFlipped ? 1 : 0,
                      transition: isFlipped ? 'opacity 0s 0.005s' : 'opacity 0s',
                      pointerEvents: isFlipped ? 'auto' : 'none',
                    }}>
                    
                    <div className="flex-1 flex flex-col justify-center items-center w-full min-h-0 overflow-y-auto custom-scrollbar px-2 mt-4">
                      <span className="text-[10px] sm:text-xs font-normal uppercase font-mono text-primary/40 dark:text-slate-400 mb-4 sm:mb-6 tracking-[0.14em] shrink-0">{backLabel}</span>
                      {/* Imagem do conceito (só baralhos de palavras em inglês, só no verso) */}
                      {isTop && (
                        <FlashcardImage
                          word={card.original}
                          enabled={Boolean(isWordDeck && (currentDeckLanguage || card.lang_code || '').toLowerCase().startsWith('en'))}
                        />
                      )}
                      {(() => {
                        if (submittedInputs[cardIdStr]) {
                          return renderComparativeTranslation(submittedInputs[cardIdStr], backText, !isCardSwapped);
                        }

                        let mainTrans = backText || '';
                        let secondaries: string[] = [];

                        if (!isCardSwapped) {
                          if (card.primary_translation) {
                            mainTrans = card.primary_translation;
                          } else if (Array.isArray(card.translations) && card.translations.length > 0) {
                            mainTrans = card.translations[0];
                          } else if (typeof card.translation === 'string' && card.translation.includes(',')) {
                            const parts = card.translation.split(',').map(s => s.trim()).filter(Boolean);
                            mainTrans = parts[0] || '';
                          }

                          if (card.secondary_translations && card.secondary_translations.length > 0) {
                            secondaries = card.secondary_translations;
                          } else if (Array.isArray(card.translations) && card.translations.length > 1) {
                            secondaries = card.translations.slice(1);
                          } else if (typeof card.translation === 'string' && card.translation.includes(',')) {
                            const parts = card.translation.split(',').map(s => s.trim()).filter(Boolean);
                            if (parts.length > 1) {
                              secondaries = parts.slice(1);
                            }
                          }
                        }

                        const isWordCard = Boolean(
                          isWordDeck ||
                          card.is_word_card ||
                          (Array.isArray(card.translations) && card.translations.length > 0 && !card.words?.length && !card.original?.includes(' '))
                        );

                        if (isWordCard && !isCardSwapped) {
                          return (
                            <div className="flex flex-col items-center justify-center w-full space-y-3">
                              <p className="text-2xl sm:text-4xl font-medium leading-tight dark:text-ink break-words w-full text-center">
                                {mainTrans || safeT('flashcards.no_translation_fallback', 'Clique para editar e adicionar tradução')}
                              </p>

                              {secondaries && secondaries.length > 0 && (
                                <p className="text-xs sm:text-sm font-normal text-slate-500/80 dark:text-slate-400/80 max-w-md mx-auto text-center leading-relaxed tracking-wide">
                                  {secondaries.join(', ')}
                                </p>
                              )}
                            </div>
                          );
                        }

                        return (
                          <div className="flex flex-col items-center justify-center w-full space-y-3">
                            <p className="text-2xl sm:text-4xl font-medium leading-tight dark:text-ink break-words w-full text-center">
                              {backText || safeT('flashcards.no_translation_fallback', 'Clique para editar e adicionar tradução')}
                            </p>

                            {frontText && (
                              <p className="text-xs sm:text-base font-normal opacity-80 text-slate-600 dark:text-slate-300 max-w-md mx-auto text-center leading-relaxed tracking-wide">
                                {frontText}
                              </p>
                            )}
                          </div>
                        );
                      })()}
                      </div>

                    {isTop && (
                      <div className="shrink-0 w-full">
                        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 sm:gap-3 w-full mt-4 sm:mt-6">
                          {[
                            { key: 'again', label: safeT('flashcards.again', 'De novo'), color: 'bg-bad', border: 'border-bad', shadow: '' },
                            { key: 'hard', label: safeT('flashcards.hard', 'Difícil'), color: 'bg-warn', border: 'border-warn', shadow: '' },
                            { key: 'good', label: safeT('flashcards.good', 'Bom'), color: 'bg-ok', border: 'border-ok', shadow: '' },
                            { key: 'easy', label: safeT('flashcards.easy', 'Fácil'), color: 'bg-tintd-sky', border: 'border-tintd-sky', shadow: '' }
                          ].map((btn) => {
                            const projection = flashcardManager.calculateNextSrsState(card, btn.key as any);
                            return (
                              <button 
                                key={btn.key}
                                disabled={isProcessingFeedback}
                                onClick={(e) => handleSrsFeedback(e, card.id.toString(), btn.key as any)} 
                                className={`group flex flex-col items-center justify-center gap-1 py-4 sm:py-6 ${btn.color} border-2 ${btn.border} text-white rounded-2xl sm:rounded-[1.75rem] active:scale-95 transition-all sm: ${btn.shadow} relative overflow-hidden select-none disabled:opacity-70 disabled:active:scale-100 disabled:cursor-not-allowed`}
                              >
                                <span className="text-[10px] sm:text-xs font-normal uppercase font-mono tracking-[0.14em] relative z-10 drop-shadow-md pointer-events-none select-none">{btn.label}</span>
                                <span className="text-[8px] sm:text-[10px] font-normal uppercase tracking-[0.14em] font-mono relative z-10 text-white/90 pointer-events-none select-none">{(projection as any).label}</span>
                                <div className="absolute inset-0 bg-white/20 translate-y-full group-hover:translate-y-0 transition-transform duration-200" />
                              </button>
                            );
                          })}
                        </div>

                        <div className="flex items-center justify-center gap-1.5 mt-4 sm:mt-6 pb-2">
                          {card.history && card.history.trim() !== '' ? (
                            card.history.split(',').map((entry: string, idx: number) => {
                              const [val] = entry.split(':');
                              // Mapping matching the buttons and backend: 1=Red, 2=Amber, 3=Emerald(Green), 4=Blue
                              const colorMap: Record<string, string> = {
                                '1': 'bg-bad',
                                '2': 'bg-warn',
                                '3': 'bg-ok',
                                '4': 'bg-tintd-sky',
                                'again': 'bg-bad',
                                'hard': 'bg-warn',
                                'good': 'bg-ok',
                                'easy': 'bg-tintd-sky',
                                'red': 'bg-bad',
                                'amber': 'bg-warn',
                                'green': 'bg-ok',
                                'emerald': 'bg-ok',
                                'blue': 'bg-tintd-sky',
                                'R': 'bg-bad',
                                'Y': 'bg-warn',
                                'G': 'bg-ok',
                                'B': 'bg-tintd-sky'
                              };
                              return (
                                <div 
                                  key={idx} 
                                  className={`w-2.5 h-2.5 sm:w-3 sm:h-3 rounded-[4px] ${colorMap[val] || 'bg-slate-200'} shadow-sm`}
                                  title={safeT('flashcards.session', 'Sessão') + ` ${idx + 1}`}
                                />
                              );
                            })
                          ) : (
                            (!card.repetitions || card.repetitions === 0) ? (
                              <div className="text-[8px] font-normal uppercase font-mono text-slate-300 tracking-[0.14em]">{safeT('flashcards.noHistory', 'Sem histórico')}</div>
                            ) : null
                          )}
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Word Translation Bubble */}
                  {isTop && selectedWordInfo && (
                    <div 
                      onClick={(e) => e.stopPropagation()}
                      className="absolute z-[80] bg-ink text-surface px-3 py-2 rounded-xl text-xs font-semibold select-none pointer-events-auto animate-in fade-in zoom-in-95 duration-200"
                      style={{ 
                        left: `${selectedWordInfo.x}px`, 
                        top: `${selectedWordInfo.y}px`,
                        transform: 'translate(-50%, -100%)'
                      }}
                    >
                      {selectedWordInfo.loading ? (
                        <div className="flex items-center gap-1">
                          <Loader2 className="w-3 h-3 animate-spin text-primary" />
                          <span>{translatingLabel}</span>
                        </div>
                      ) : (
                        <span>{selectedWordInfo.translation}</span>
                      )}
                      <div 
                        className="absolute bottom-0 left-1/2 -translate-x-1/2 translate-y-full w-0 h-0 border-x-4 border-x-transparent border-t-4 border-t-slate-900 dark:border-t-white"
                      />
                    </div>
                  )}
                </div>

                {/* External Action Bar ABAIXO do card (Imagem 3) */}
                {isTop && (
                  <div className="w-full flex items-center justify-between px-2 mt-4 z-50">
                    {/* Left side: trocar + voltar */}
                    <div className="flex items-center gap-2">
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          toggleSwapDeck();
                        }}
                        className={`p-3 sm:p-4 rounded-2xl hover:scale-105 active:scale-95 transition-all flex items-center justify-center ${
                          isCardSwapped 
                            ? 'bg-ink text-surface' 
                            : 'bg-surface dark:bg-slate-800 text-slate-400 hover:text-primary border border-black/[0.06] dark:border-slate-700'
                        }`}
                        title={i18nInstance.t('ui.fc.flip')}
                      >
                        <ArrowLeftRight size={18} />
                      </button>

                      {cardHistoryStack.length > 0 && (
                        <button
                          type="button"
                          onClick={handleUndoLastCard}
                          className="p-3 sm:p-4 bg-surface dark:bg-slate-800 text-slate-500 hover:text-primary rounded-2xl hover:scale-105 active:scale-95 transition-all flex items-center justify-center border border-black/[0.06] dark:border-slate-700"
                          title="Voltar para o flashcard anterior"
                        >
                          <RotateCcw size={18} />
                        </button>
                      )}
                    </div>

                    {/* Right side: + (expandir) + reproduzir */}
                    <div className="flex items-center gap-2">
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          const txt = isFlipped ? backText : frontText;
                          setShowFullText({ id: cardIdStr, text: txt, type: isFlipped ? (isCardSwapped ? 'original' : 'translation') : (isCardSwapped ? 'translation' : 'original') });
                        }}
                        className="p-3 sm:p-4 bg-surface dark:bg-slate-800 text-slate-400 hover:text-primary rounded-2xl hover:scale-105 active:scale-95 transition-all border border-black/[0.06] dark:border-slate-700 flex items-center justify-center"
                        title="Expandir texto"
                      >
                        <Plus size={18} />
                      </button>

                      <button
                        type="button"
                        onClick={(e) => handleAudio(e, card, isFlipped ? isCardSwapped : !isCardSwapped)}
                        disabled={isLoading}
                        className="p-3 sm:p-4 bg-surface dark:bg-slate-800 text-primary rounded-2xl hover:scale-105 active:scale-95 transition-all border border-black/[0.06] dark:border-slate-700 disabled:opacity-50 flex items-center justify-center"
                        title={isLoading ? "Parar áudio" : "Reproduzir áudio"}
                      >
                        {isLoading ? <Square size={18} className="fill-primary text-primary" /> : <Volume2 size={18} />}
                      </button>
                    </div>
                  </div>
                )}

                {isTop && (
                  <button 
                    onClick={(e) => { e.stopPropagation(); onDelete(card.id.toString()); }} 
                    className="absolute -top-4 -right-4 p-4 bg-surface text-slate-300 hover:text-bad rounded-full shadow-[0_24px_60px_-24px_rgba(0,0,0,0.35)] opacity-0 group-hover:opacity-100 transition-all z-[110] border border-black/[0.06] dark:border-white/[0.06] scale-110 active:scale-90"
                  >
                    <Trash2 size={20} />
                  </button>
                )}
              </motion.div>
            );
          })
        ) : deckPendingSrsCards.length > 0 ? (
          <motion.div 
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="flex flex-col items-center justify-center space-y-5 text-center p-8 bg-surface rounded-[28px] border border-black/[0.06] dark:border-white/[0.06] w-full max-w-md mx-auto"
          >
             <div className="w-20 h-20 bg-primary/10 text-primary rounded-3xl flex items-center justify-center animate-pulse">
               <Brain size={40} />
             </div>
             <div>
               <h3 className="text-xl font-medium text-ink tracking-[-0.02em]"><T k="ui.fc.waitingReview" /></h3>
               <p className="text-slate-500 dark:text-slate-400 text-xs font-bold max-w-[280px] mt-2 leading-relaxed">
                 Você concluiu as cartas novas. As cartas em fila (<span className="text-warn"><T k="ui.fc.againHard" /></span>) retornarão automaticamente em breve.
               </p>
             </div>

             {(() => {
               const sorted = [...deckPendingSrsCards].sort((a, b) => (a.reviewDueAtTimestamp || 0) - (b.reviewDueAtTimestamp || 0));
               const nextDueCard = sorted[0];
               const diffMs = Math.max(0, (nextDueCard?.reviewDueAtTimestamp || Date.now()) - nowTimestamp);
               const totalSec = Math.ceil(diffMs / 1000);
               const mins = Math.floor(totalSec / 60);
               const secs = totalSec % 60;
               const formattedTimer = `${String(mins).padStart(2, '0')}:${String(secs).padStart(2, '0')}`;

               const handleForceReviewNow = () => {
                 const now = Date.now();
                 const forcedCard = { ...nextDueCard, reviewDueAtTimestamp: now };
                 setPendingSrsCards(prev => prev.filter(c => String(c.id) !== String(nextDueCard.id)));
                 setLocalQueue([forcedCard]);
               };

               return (
                 <div className="w-full flex flex-col items-center gap-4">
                   <div className="bg-slate-100 dark:bg-slate-800 px-8 py-4 rounded-2xl border border-black/[0.06] dark:border-slate-700/60">
                     <span className="text-[10px] uppercase font-mono font-normal tracking-[0.14em] text-slate-400 block mb-1">Tempo Restante</span>
                     <span className="text-4xl font-medium font-mono text-primary dark:text-primary-light">
                       {formattedTimer}
                     </span>
                   </div>

                   <button
                     onClick={handleForceReviewNow}
                     className="px-6 py-3 bg-primary text-slate-900 rounded-2xl font-medium text-[10px] hover:opacity-90 active:scale-95 transition-all flex items-center gap-2"
                   >
                     <RotateCcw size={14} />
                     Revisar Agora Mesmo
                   </button>
                 </div>
               );
             })()}
          </motion.div>
        ) : (
          <motion.div 
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="flex flex-col items-center justify-center space-y-6 text-center py-12"
          >
             <div className="w-24 h-24 bg-ok/10 text-ok rounded-full flex items-center justify-center animate-bounce">
               <Check size={48} />
             </div>
             <div>
               <h3 className="text-2xl font-medium dark:text-ink">{safeT('flashcards.allDone', 'Tudo pronto!')}</h3>
               <p className="text-slate-500 dark:text-slate-400 font-bold max-w-[280px] mt-2">{safeT('flashcards.allCardsReviewed', 'Você revisou todos os cartões disponíveis para este baralho.')}</p>
             </div>
             <button 
               onClick={() => onDeckCompleted?.()}
               className="mt-6 px-10 py-5 bg-primary text-slate-900 rounded-[24px] font-medium text-[13px] active:scale-95 transition-all"
             >
               {safeT('flashcards.finishReview', 'Finalizar Revisão')}
             </button>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

const FlashcardsPage: React.FC = () => {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const safeT = (key: string, fallback: string) => {
    const res = t(key);
    return res === key ? fallback : res;
  };
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const isGuide = location.pathname.includes('/guide') || searchParams.get('from') === 'guide';
  const { isDarkMode, toggleAppOnlyTheme, targetLanguage } = useTheme();
  const [appNotification, setAppNotification] = useState<{ show: boolean, title: string, message: string, type: 'success' | 'error' | 'info' }>({ show: false, title: '', message: '', type: 'success' });
  const [activeTab, setActiveTab] = useState<'flashcards' | 'favorites'>('flashcards');
  const { profile } = useData() as any; // Access profile for streaks
  const [flashcards, setFlashcards] = useState<Flashcard[]>([]);
  const [isLoadingDecks, setIsLoadingDecks] = useState(true);
  const [isSyncing, setIsSyncing] = useState(false);
  useStreakCounter(activeTab === 'flashcards' && flashcards.length > 0);
  const [selectedLanguageDeck, setSelectedLanguageDeck] = useState<string | null>(null);
  const [isDashboardVisible, setIsDashboardVisible] = useState(true);
  const [favoriteDecks, setFavoriteDecks] = useState<string[]>(() => JSON.parse(localStorage.getItem('libritui_favorite_decks') || '[]'));
  const [selectedDeckFilter, setSelectedDeckFilter] = useState<string>('all');
  const [currentDeckPage, setCurrentDeckPage] = useState(1);
  const [isDeckFilterOpen, setIsDeckFilterOpen] = useState<boolean>(false);
  const filterDropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setCurrentDeckPage(1);
  }, [selectedDeckFilter, selectedLanguageDeck]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (filterDropdownRef.current && !filterDropdownRef.current.contains(event.target as Node)) {
        setIsDeckFilterOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  const [isImporting, setIsImporting] = useState(false);
  const [txtImportRawLines, setTxtImportRawLines] = useState<string[]>([]);
  const [showTxtImportModal, setShowTxtImportModal] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Estados para modais e ações
  const [audioLoadingId, setAudioLoadingId] = useState<string | null>(null);
  const [deckToDelete, setDeckToDelete] = useState<string | null>(null);
  const [deckToRename, setDeckToRename] = useState<string | null>(null);
  const [deckCompleted, setDeckCompleted] = useState(false); // Shows 'deck completed!' modal after last card
  const [isLanguageSelectorOpen, setIsLanguageSelectorOpen] = useState(false);
  const [deckBeingConfigured, setDeckBeingConfigured] = useState<string | null>(null);
  const [deckLanguageMappings, setDeckLanguageMappings] = useState<Record<string, string>>(() => {
    try {
      return JSON.parse(localStorage.getItem('libritui_deck_languages') || '{}');
    } catch { return {}; }
  });
  const [allReviewedDeck, setAllReviewedDeck] = useState<string | null>(null);

  const handleOpenLanguageSelector = (e: React.MouseEvent, deckName: string) => {
    e.stopPropagation();
    setDeckBeingConfigured(deckName);
    setIsLanguageSelectorOpen(true);
  };

  const saveDeckLanguage = (langCode: string) => {
    if (!deckBeingConfigured) return;
    const newMappings = { ...deckLanguageMappings, [deckBeingConfigured]: langCode };
    setDeckLanguageMappings(newMappings);
    localStorage.setItem('libritui_deck_languages', JSON.stringify(newMappings));
    setIsLanguageSelectorOpen(false);
    setDeckBeingConfigured(null);
  };

  const [openDeckMenuId, setOpenDeckMenuId] = useState<string | null>(null);

  useEffect(() => {
    const handleClickOutside = () => setOpenDeckMenuId(null);
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, []);

  const [editingDeck, setEditingDeck] = useState<{ deckId: string, name: string, langCode: string, cards: Flashcard[] } | null>(null);
  const [selectedCardsForDeletion, setSelectedCardsForDeletion] = useState<Set<string>>(new Set());
  const [isSelectionMode, setIsSelectionMode] = useState(false);
  const [editingVisibleCount, setEditingVisibleCount] = useState(20);
  const [showConfirmBatchDelete, setShowConfirmBatchDelete] = useState(false);

  const handleEditDeck = (e: React.MouseEvent, deckId: string, deckName: string, langCode: string, cards: Flashcard[]) => {
    e.stopPropagation();
    setOpenDeckMenuId(null);
    setEditingDeck({ deckId, name: deckName, langCode, cards });
    setIsSelectionMode(false);
    setSelectedCardsForDeletion(new Set());
    setEditingVisibleCount(20);
  };

  const handleExportDeckTxt = (e: React.MouseEvent, deckInfo: any) => {
    e.stopPropagation();
    setOpenDeckMenuId(null);
    const cardsToExport = deckInfo.cards || [];
    if (cardsToExport.length === 0) {
      setAppNotification({
        show: true,
        title: 'Baralho Vazio',
        message: 'Este baralho não possui flashcards para exportar.',
        type: 'error'
      });
      return;
    }
    const lines = cardsToExport.map((c: any) => `${(c.original || '').trim()}\t${(c.translation || '').trim()}`);
    const content = lines.join('\n');
    const blob = new Blob([content], { type: 'text/plain;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    const safeName = (deckInfo.name || 'baralho').replace(/[/\\?%*:|"<>]/g, '_');
    a.download = `${safeName}.txt`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    setAppNotification({
      show: true,
      title: 'Baralho Exportado',
      message: `Baralho "${deckInfo.name}" salvo no dispositivo em formato TXT!`,
      type: 'success'
    });
  };

  const handleScrollEditView = (e: React.UIEvent<HTMLDivElement>) => {
    const bottom = e.currentTarget.scrollHeight - e.currentTarget.scrollTop <= e.currentTarget.clientHeight + 100;
    if (bottom && editingDeck && editingVisibleCount < editingDeck.cards.length) {
      setEditingVisibleCount(prev => prev + 20);
    }
  };

  const [studyLogs, setStudyLogs] = useState<any[]>([]);
  const [isStatsLoading, setIsStatsLoading] = useState(false);
  const [filterLang, setFilterLang] = useState('all');
  const [filterDeck, setFilterDeck] = useState('all');
  const [filterPeriod, setFilterPeriod] = useState<'Anos' | 'Semanas' | 'Dias'>('Dias');
  const [allDecks, setAllDecks] = useState<any[]>([]);
  const [downloadingDecks, setDownloadingDecks] = useState<Record<string, boolean>>({});
  const downloadingDecksRef = useRef<Record<string, boolean>>({});
  
  const fetchChartData = async () => {
    const user = await supabase.auth.getSession().then(({data}) => data.session?.user);
    if (!user) return;
    setIsStatsLoading(true);
    try {
      const response = await dbManager.execute({
        action: 'get_flashcards_chart_stats',
        data: {
          userId: user.id,
          langCode: filterLang,
          deckId: filterDeck,
          period: filterPeriod
        }
      });
      if (response) {
        setStudyLogs(response.data || response);
      }
    } catch (e) {
      console.error('Failed to fetch chart data', e);
    } finally {
      setTimeout(() => setIsStatsLoading(false), 250);
    }
  };

  useEffect(() => {
     fetchChartData();
     const effectiveUserId = user?.id || (typeof localStorage !== 'undefined' ? localStorage.getItem('libritui_user_id') || undefined : undefined);
     const selectedDeckObj = allDecks.find(d => String(d.id) === String(filterDeck));

     const actualLang = (
       selectedDeckObj?.target_lang || 
       selectedDeckObj?.lang_code || 
       selectedDeckObj?.idioma_alvo || 
       (filterLang !== 'all' ? filterLang : null) || 
       profile?.learning_language || 
       (typeof localStorage !== 'undefined' ? localStorage.getItem('libritui_learning_lang') || localStorage.getItem('current_language') : '') || 
       ''
     ).toString().trim().split('-')[0].split('_')[0].toLowerCase();

     const safeLang = (actualLang && actualLang !== 'all' && actualLang.length >= 2) ? actualLang : null;

     if (effectiveUserId && safeLang) {
       activeStudyTracker.setUserContext(effectiveUserId, safeLang);
       activeStudyTracker.startSession(safeLang);
     }
     return () => {
       activeStudyTracker.flush();
     };
  }, [filterLang, filterDeck, filterPeriod, user?.id, allDecks]);

  const loadLogs = fetchChartData;

  const [importProgress, setImportProgress] = useState<{current: number, total: number} | null>(null);

  const fetchDecks = useCallback(async (force = false) => {
    const session = await supabase.auth.getSession();
    const user = session.data.session?.user;
    if (!user) {
      setIsLoadingDecks(false);
      return;
    }

    // Load from cache first
    let hasCache = false;
    const cachedDecks = localStorage.getItem(`libritui_cache_all_decks_${user.id}`);
    const cachedFavs = localStorage.getItem(`libritui_cache_favs_${user.id}`);

    // Remove the large cards cache from localStorage to prevent QuotaExceededError
    localStorage.removeItem(`libritui_cache_all_cards_${user.id}`);

    if (cachedDecks) {
      setAllDecks(JSON.parse(cachedDecks));
      hasCache = true;
    }
    if (cachedFavs) {
      const parsedFavs = JSON.parse(cachedFavs);
      setFavoriteDecks(parsedFavs);
      localStorage.setItem('libritui_favorite_decks', JSON.stringify(parsedFavs));
    }

    if (!hasCache) {
      setIsLoadingDecks(true);
    } else {
      setIsLoadingDecks(false);
    }

    // Now fetch fresh data in background
    try {
      const dks = await dbManager.from('decks', user.id).select({ type: 'flashcard' });
      
      // Fetch virtual imported decks
      // Fetch virtual imported decks (using local cache support)
      let importedDecks: any[] = [];
      let importedList: any[] = [];
      try {
        const cacheImportedKey = `libritui_cache_d1_flashcards_imported_${user.id}`;
        const cachedImportedStr = localStorage.getItem(cacheImportedKey);
        if (cachedImportedStr) {
          importedList = JSON.parse(cachedImportedStr);
        } else {
          const importedRes = await dbManager.from('flashcards_imported', user.id).select({ user_id: user.id });
          importedList = Array.isArray(importedRes) ? importedRes : (importedRes?.data || []);
          localStorage.setItem(cacheImportedKey, JSON.stringify(importedList));
        }

        importedDecks = importedList.map((deck: any) => ({
          id: `${deck.deck_id}_${deck.idioma_nativo}_${deck.idioma_alvo}`,
          user_id: user.id,
          name: `Nível ${deck.deck_id} (${deck.idioma_nativo.toUpperCase()} → ${deck.idioma_alvo.toUpperCase()})`,
          type: 'flashcard_imported',
          lang_code: deck.idioma_alvo,
          created_at: deck.created_at,
          caminho_local_do_arquivo: deck.caminho_local_do_arquivo
        }));
      } catch (e) {
        console.error('[FlashcardsPage] Failed to fetch imported decks:', e);
      }
      
      const freshDecks = [...(dks || []), ...importedDecks];
      setAllDecks(freshDecks);
      localStorage.setItem(`libritui_cache_all_decks_${user.id}`, JSON.stringify(freshDecks));

      // Extract favoritism info from D1 to sync local state
      const favs: string[] = [];
      (dks || []).forEach((d: any) => {
        if (d.favorited === 1 || d.favorited === true) {
          favs.push(String(d.id));
        }
      });
      importedList.forEach((d: any) => {
        if (d.favorito === 1 || d.favorito === true) {
          favs.push(`${d.deck_id}_${d.idioma_nativo}_${d.idioma_alvo}`);
        }
      });
      setFavoriteDecks(favs);
      localStorage.setItem(`libritui_cache_favs_${user.id}`, JSON.stringify(favs));
      localStorage.setItem('libritui_favorite_decks', JSON.stringify(favs));

    } catch (err) {
      console.error('[FlashcardsPage] Failed to fetch fresh decks:', err);
    }

    try {
      const cards = await flashcardManager.getFlashcards();
      setFlashcards(cards);
    } catch (err) {
      console.error('[FlashcardsPage] Failed to fetch fresh cards:', err);
    }

    loadLogs();
    setIsLoadingDecks(false);
  }, []);

  const refreshData = async () => {
    setIsSyncing(true);
    await flashcardManager.processPersistentQueue();
    await fetchDecks(true);
    setIsSyncing(false);
  };

  useEffect(() => {
    fetchDecks();

    const handleUpdate = (e: any) => {
        if (e.detail?.field === 'cards_reviewed') {
            const { amount, date, meta } = e.detail;
            
            // SÓ incrementa o gráfico local se o baralho estudado corresponder ao filtro ativo na tela
            if (filterDeck === 'all' || filterDeck === meta?.deck_id) {
              console.log(`[Flashcards] Optimistic stats update: +${amount} for ${date}`);
              if (typeof setStudyLogs === 'function') {
                  setStudyLogs(prev => {
                      const next = [...prev];
                      const logIdx = next.findIndex(l => l.activity_date === date);
                      if (logIdx !== -1) {
                          next[logIdx] = { ...next[logIdx], cards_reviewed: (next[logIdx].cards_reviewed || 0) + (amount || 1) };
                      } else {
                          next.unshift({ activity_date: date, cards_reviewed: amount || 1 });
                      }
                      return next;
                  });
              }
            } else {
              console.log(`[Flashcards] Optimistic stats skipped: filterDeck(${filterDeck}) mismatch with card deck(${meta?.deck_id})`);
            }
        }
    };

    window.addEventListener('libritui-stats-updated', handleUpdate);
    
    const handleImportProgress = (e: any) => {
      setImportProgress(e.detail);
      setIsImporting(false); // Disable normal spinner if queue is active
    };
    const handleImportComplete = () => {
      setImportProgress(null);
      refreshData();
    };

    window.addEventListener('importQueueProgress', handleImportProgress);
    window.addEventListener('importQueueComplete', handleImportComplete);

    // Resume queue if any
    flashcardImportQueueService.processQueue();
    const q = flashcardImportQueueService.getQueue();
    if (q.length > 0) {
      setImportProgress({ current: q[0].total - q[0].cards.length, total: q[0].total });
    }

    return () => {
        window.removeEventListener('libritui-stats-updated', handleUpdate);
        window.removeEventListener('importQueueProgress', handleImportProgress);
        window.removeEventListener('importQueueComplete', handleImportComplete);
    };
  }, []);

  const flashcardDecks = useMemo(() => {
    const decks: Record<string, { id: string, name: string, cards: Flashcard[], dueCount: number, deckData?: any }> = {};
    const today = getLocalISOString().split('T')[0];

    // Initialize exclusively with all decks from backend
    allDecks.forEach(d => {
      const dId = String(d.id);
      decks[dId] = { 
        id: dId,
        name: d.name || 'Geral', 
        cards: [], 
        dueCount: 0, 
        deckData: d 
      };
    });

    flashcards.forEach(f => {
      const dId = f.deck_id ? String(f.deck_id) : 'unknown';
      if (decks[dId]) {
        decks[dId].cards.push(f);
        const isDue = !f.next_review_date || f.next_review_date <= today;
        if (isDue) decks[dId].dueCount++;
      }
    });
    return decks;
  }, [flashcards, allDecks]);

  // Auto-select deck if launched from Study Plan guide (only once upon mounting or first load)
  const hasAutoSelectedRef = useRef(false);
  const { langCode: routeLang, level: routeLevel } = useParams<{ langCode?: string; level?: string }>();

  useEffect(() => {
    if (hasAutoSelectedRef.current) return;

    const targetDeckId = searchParams.get('deck');
    const mode = searchParams.get('mode');
    const level = routeLevel || searchParams.get('level');
    const lang = routeLang ? getLanguageCode(routeLang) : searchParams.get('lang');

    // Baralho da loja da missão: palavras = "words_A1_<nativo>_<alvo>", frases = "A1_<nativo>_<alvo>"
    const suffix = lang ? `_${String(lang).toLowerCase()}` : '';
    const findByPrefix = (prefix: string) => Object.values(flashcardDecks).find(d => {
      const id = d.id.toLowerCase();
      return id.startsWith(prefix.toLowerCase()) && (!suffix || id.endsWith(suffix));
    });

    if (targetDeckId) {
      const exact = flashcardDecks[targetDeckId]
        || Object.values(flashcardDecks).find(d => d.id.toLowerCase() === targetDeckId.toLowerCase());
      if (exact) {
        hasAutoSelectedRef.current = true;
        setSelectedLanguageDeck(exact.id);
        return;
      }
      // Id completo vindo da missão: abre já, a lista de baralhos termina de carregar em seguida
      if (suffix && targetDeckId.toLowerCase().endsWith(suffix)) {
        hasAutoSelectedRef.current = true;
        setSelectedLanguageDeck(targetDeckId);
        return;
      }
      // Id base ("words_A1"): procura o baralho do idioma
      const byBase = findByPrefix(`${targetDeckId}_`);
      if (byBase) {
        hasAutoSelectedRef.current = true;
        setSelectedLanguageDeck(byBase.id);
      }
      return;
    }

    if (!lang || !level || (mode !== 'words' && mode !== 'phrases')) return;
    const found = findByPrefix(mode === 'words' ? `words_${level}_` : `${level}_`);
    if (found) {
      hasAutoSelectedRef.current = true;
      setSelectedLanguageDeck(found.id);
    }
  }, [searchParams, flashcardDecks]);


  // TOUR LOGIC
  useEffect(() => {
    if (tourService.isActive() && tourService.getPhase() === 'FLASHCARDS' && !selectedLanguageDeck) {
      setTimeout(() => {
        tourService.createDriver([
          {
            element: '#tour-flashcard-deck',
            popover: {
              title: 'O seu Baralho!',
              description: 'As palavras que você salvar virão para cá. Clique no seu baralho para iniciar o estudo.',
              showButtons: []
            }
          }
        ]);
      }, 800);
    } else if (tourService.isActive() && tourService.getPhase() === 'FLASHCARDS' && selectedLanguageDeck) {
      setTimeout(() => {
        tourService.setPhase('GOTO_PRONUNCIATION');
        tourService.createDriver([
          {
            element: '#tour-flashcards-back',
            popover: {
              title: 'Ótimo trabalho!',
              description: 'Aqui você vira as cartas e avalia sua dificuldade. Volte ao Hub para irmos à próxima ferramenta: Pronúncia!',
              showButtons: []
            }
          }
        ]);
      }, 800);
    }
  }, [selectedLanguageDeck, flashcardDecks]);

  const toggleFavoriteDeck = async (e: React.MouseEvent, lang: string) => {
    e.stopPropagation();
    const isFav = favoriteDecks.includes(lang);
    const next = isFav ? favoriteDecks.filter(l => l !== lang) : [...favoriteDecks, lang];
    setFavoriteDecks(next);
    localStorage.setItem('libritui_favorite_decks', JSON.stringify(next));

    const user = await supabase.auth.getSession().then(({data}) => data.session?.user);
    if (user) {
      try {
        localStorage.setItem(`libritui_cache_favs_${user.id}`, JSON.stringify(next));
        
        const parts = lang.split('_');
        if (parts.length === 3) {
          // Imported deck
          const [deckId, native, target] = parts;
          await dbManager.from('flashcards_imported', user.id).update(
            { favorito: isFav ? 0 : 1 },
            { deck_id: deckId, idioma_nativo: native, idioma_alvo: target }
          );
        } else {
          // Standard deck
          await dbManager.from('decks', user.id).update(
            { favorited: isFav ? 0 : 1 },
            { id: lang }
          );
        }
      } catch (err) {
        console.error('[FlashcardsPage] Failed to save favorite status to D1:', err);
      }
    }
  };


  /**
   * handleImportTxtFileSelect: Importação de Notas em Texto Puro (.txt).
   * SEMPRE cria um novo baralho com o nome do arquivo importado, evitando mesclagem.
   */
  const handleImportTxtFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    try {
      importRateLimiter.checkLimit();
      if (file.size > 15 * 1024 * 1024) {
        throw new Error("O arquivo é muito grande. O limite para flashcards é de 15MB.");
      }

      setIsImporting(true);
      const text = await file.text();
      const lines = text.split(/\r?\n/);
      
      const validLines = lines.filter(line => {
        if (!line.trim()) return false;
        const parts = line.split('\t');
        return parts.length >= 2 && parts[0].trim() !== '' && parts[1].trim() !== '';
      });

      if (validLines.length > 0) {
        setTxtImportRawLines(validLines);
        setShowTxtImportModal(true);
      } else {
        setAppNotification({
          show: true,
          title: t('flashcards.import_error_title'),
          message: t('flashcards.import_empty_file_message'),
          type: 'error'
        });
      }
    } catch (err: any) {
      console.error("[Deck Import] Erro no processamento local:", err);
      setAppNotification({
        show: true,
        title: t('flashcards.import_error_title'),
        message: t('flashcards.import_error_message', { message: err.message }),
        type: 'error'
      });
    } finally {
      setIsImporting(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleTxtImportConfirm = async (category: string, langCode: string) => {
    setShowTxtImportModal(false);
    
    let finalLangCode = langCode;
    const targetDeckId = crypto.randomUUID(); // ID único para o novo baralho
    let finalDeckId: string | null = null;
    
    const isNewDeck = !flashcardDecks[category];

    if (isNewDeck) {
      if (!user) return;
      
      const now = new Date().toISOString();
      try {
        await dbManager.from('decks', user.id).insert({
          id: targetDeckId,
          user_id: user.id,
          name: category,
          lang_code: finalLangCode,
          type: 'flashcard',
          created_at: now,
          updated_at: now
        });
      } catch (error) {
        console.error("[Deck Import] Failed to create new deck", error);
        setAppNotification({
          show: true,
          title: "Erro",
          message: "Não foi possível criar o baralho.",
          type: "error"
        });
        return;
      }
      finalDeckId = targetDeckId;
    } else {
      finalLangCode = flashcardDecks[category]?.cards[0]?.lang_code || 'en';
      finalDeckId = flashcardDecks[category]?.cards[0]?.deck_id || null;
    }
    
    // Process the lines with the explicit category and langCode
    const finalCards = txtImportRawLines.map(line => {
      const parts = line.split('\t');
      return {
        original: parts[0].trim(),
        translation: parts[1].trim(),
        lang_code: finalLangCode,
        category: category,
        deck_id: isNewDeck ? targetDeckId : finalDeckId
      };
    });

    await flashcardImportQueueService.enqueue(finalCards);
    setAppNotification({
      show: true,
      title: t('flashcards.import_success_title', 'Importado com sucesso'),
      message: 'Sua importação está na fila. Você pode navegar no aplicativo enquanto preparamos tudo pra você!',
      type: 'success'
    });
    setTxtImportRawLines([]);
  };

  const handleOpenDeleteModal = (e: React.MouseEvent, lang: string) => {
    e.stopPropagation();
    setDeckToDelete(lang);
  };

const DELETE_NOTIFICATION_TRANSLATIONS: Record<string, { title: string; message: string }> = {
  pt: { title: "Baralho excluído!", message: "" },
  en: { title: "Deck deleted!", message: "" },
  es: { title: "¡Mazo eliminado!", message: "" },
  fr: { title: "Paquet supprimé !", message: "" },
  de: { title: "Stapel gelöscht!", message: "" },
  it: { title: "Mazzo eliminato!", message: "" },
  hu: { title: "Pakli törölve!", message: "" },
  ja: { title: "デッキが削除されました！", message: "" },
  zh: { title: "卡片集已删除！", message: "" },
  ru: { title: "Колода удалена!", message: "" }
};

  const executeDeleteDeckDirect = async (lang: string) => {
    // 0. Instantly close the confirmation modal
    setDeckToDelete(null);

    // 1. Instantly hide the deck visually
    setFlashcards(prev => prev.filter(f => f.deck_id !== lang && f.category !== lang && (f.category || f.lang_code || 'Geral') !== lang));
    setAllDecks(prev => prev.filter(d => d.id !== lang && d.name !== lang));

    if (favoriteDecks.includes(lang)) {
      const nextFavs = favoriteDecks.filter(l => l !== lang);
      setFavoriteDecks(nextFavs);
      localStorage.setItem('libritui_favorite_decks', JSON.stringify(nextFavs));
    }

    // Clear local storage caches for user
    const session = await supabase.auth.getSession();
    const user = session.data.session?.user;
    if (user) {
      localStorage.removeItem(`libritui_cache_d1_flashcards_imported_${user.id}`);
      localStorage.removeItem(`libritui_cache_all_decks_${user.id}`);
      localStorage.removeItem(`libritui_cache_all_cards_${user.id}`);
      if (favoriteDecks.includes(lang)) {
        const nextFavs = favoriteDecks.filter(l => l !== lang);
        localStorage.setItem(`libritui_cache_favs_${user.id}`, JSON.stringify(nextFavs));
      }
    }

    const currentLang = (i18n.language || 'pt').split('-')[0].toLowerCase();
    const trans = DELETE_NOTIFICATION_TRANSLATIONS[currentLang] || DELETE_NOTIFICATION_TRANSLATIONS.pt;

    // 2. Show success notification immediately with title only
    setAppNotification({
      show: true,
      title: trans.title,
      message: '',
      type: 'success'
    });

    setIsSyncing(true);
    
    // 3. Await D1 deck deletion in background
    try {
      await flashcardManager.deleteFlashcardDeck(lang);
    } catch (e) {
      console.error("[FlashcardsPage] Failed to delete deck from D1:", e);
    } finally {
      if (user) {
        localStorage.removeItem(`libritui_cache_d1_flashcards_imported_${user.id}`);
        localStorage.removeItem(`libritui_cache_all_decks_${user.id}`);
      }
      setIsSyncing(false);
    }
  };

  const executeDeleteDeck = async () => {
    if (!deckToDelete) return;
    const lang = deckToDelete;
    setDeckToDelete(null);
    executeDeleteDeckDirect(lang);
  };

  const executeRenameDeck = async (newName: string) => {
    if (!deckToRename || !newName.trim()) return;
    const lang = deckToRename;
    const trimmedNewName = newName.trim();

    if (trimmedNewName === lang) { // No actual change
      setDeckToRename(null);
      return;
    }

    // Check if newName already exists as a deck
    if (flashcardDecks[trimmedNewName]) {
      setAppNotification({
        show: true,
        title: 'Nome já existe',
        message: 'Já existe um baralho com esse nome.',
        type: 'error'
      });
      return;
    }

    // Bulk rename in D1
    await flashcardManager.renameFlashcardDeck(lang, trimmedNewName);

    if (favoriteDecks.includes(lang)) {
      const nextFavs = favoriteDecks.map(l => l === lang ? trimmedNewName : l);
      setFavoriteDecks(nextFavs);
      localStorage.setItem('libritui_favorite_decks', JSON.stringify(nextFavs));
    }
    await refreshData();
    setDeckToRename(null);
    setAppNotification({
      show: true,
      title: 'Baralho renomeado!',
      message: 'Baralho renomeado com sucesso.',
      type: 'success'
    });
  };

  const favoriteDecksList = useMemo(() =>
    Object.values(flashcardDecks).filter((deckInfo) => favoriteDecks.includes(deckInfo.id)),
    [flashcardDecks, favoriteDecks]);

  const availableDeckLanguages = useMemo(() => {
    const langSet = new Set<string>();
    Object.values(flashcardDecks).forEach(deck => {
      const lang = deck.cards[0]?.lang_code || deck.deckData?.lang_code || 'en';
      if (lang) langSet.add(lang.toLowerCase());
    });
    return Array.from(langSet);
  }, [flashcardDecks]);

  const filteredFlashcardDecks = useMemo(() => {
    let list = Object.values(flashcardDecks);
    if (selectedDeckFilter === 'favorites') {
      list = list.filter(deckInfo => favoriteDecks.includes(deckInfo.id));
    } else if (selectedDeckFilter !== 'all') {
      list = list.filter(deckInfo => {
        const lang = deckInfo.cards[0]?.lang_code || deckInfo.deckData?.lang_code || 'en';
        return lang.toLowerCase() === selectedDeckFilter.toLowerCase();
      });
    }
    return list;
  }, [flashcardDecks, selectedDeckFilter, favoriteDecks]);

  const displayedFlashcards = useMemo(() => {
    let list = flashcards;
    if (selectedLanguageDeck) {
      list = list.filter(f => String(f.deck_id) === String(selectedLanguageDeck));
    }
    // Always show ALL cards (user can still study even with 0 due)
    return list;
  }, [flashcards, selectedLanguageDeck]);

  const handleOpenDeck = (lang: string) => {
    setSelectedLanguageDeck(lang);
  };

  const handleBatchDelete = () => {
    const idsToDelete = Array.from(selectedCardsForDeletion);
    if (idsToDelete.length === 0) return;

    setFlashcards(prev => prev.filter(c => !idsToDelete.includes(String(c.id))));
    if (editingDeck) {
       setEditingDeck(prev => prev ? { ...prev, cards: prev.cards.filter(c => !idsToDelete.includes(String(c.id))) } : null);
    }
    
    flashcardManager.enqueueDeletion(idsToDelete);

    setShowConfirmBatchDelete(false);
    setIsSelectionMode(false);
    setSelectedCardsForDeletion(new Set());
  };

  return (
    <div className="space-y-8 animate-in fade-in slide-in-from-bottom-2 duration-500 pb-24 px-4 md:px-0">
      {!editingDeck && !selectedLanguageDeck && (
        <>
          <div className="mb-4">
            <AppBackButton
              id="tour-flashcards-back"
              onClick={() => isGuide ? navigate('/') : goBackOr(navigate, '/study-hub')}
              fallbackTo={isGuide ? '/' : '/study-hub'}
            />
          </div>

          <StudyPageHeader
            tourKey="flashcards"
            title={String(safeT('flashcards.title', 'Flashcards'))}
            actions={
              <span data-tour="flashcards-actions" className="flex items-center gap-2">
                <button
                  onClick={() => navigate('/flashcards/store')}
                  className="w-10 h-10 rounded-full bg-surface border border-black/[0.06] dark:border-white/[0.06] flex items-center justify-center text-ink hover:bg-tint-butter transition-colors cursor-pointer"
                  title="Loja de Flashcards"
                >
                  <AnimatedIcon motion="bounce"><Store size={18} strokeWidth={1.75} /></AnimatedIcon>
                </button>
                <button
                  onClick={() => fileInputRef.current?.click()}
                  disabled={isImporting}
                  className="w-10 h-10 rounded-full bg-surface border border-black/[0.06] dark:border-white/[0.06] flex items-center justify-center text-ink hover:bg-tint-sky transition-colors cursor-pointer"
                  title={t('flashcards.import_anki_tooltip')}
                >
                  {isImporting ? <Loader2 className="animate-spin" size={18} /> : <AnimatedIcon motion="pop"><FileUp size={18} strokeWidth={1.75} /></AnimatedIcon>}
                </button>
                <input
                  type="file"
                  ref={fileInputRef}
                  accept=".txt"
                  className="hidden"
                  onChange={handleImportTxtFileSelect}
                />
                <button
                  onClick={() => setIsDashboardVisible(prev => !prev)}
                  className="w-10 h-10 rounded-full bg-surface border border-black/[0.06] dark:border-white/[0.06] flex items-center justify-center text-muted hover:text-ink hover:bg-tint-stone transition-colors cursor-pointer"
                  title={isDashboardVisible ? 'Ocultar Dashboard' : 'Mostrar Dashboard'}
                >
                  <AnimatedIcon motion="flip">{isDashboardVisible ? <Eye size={18} strokeWidth={1.75} /> : <EyeOff size={18} strokeWidth={1.75} />}</AnimatedIcon>
                </button>
              </span>
            }
          />
        </>
      )}

      <ConfirmationModal
        isOpen={showConfirmBatchDelete}
        title={safeT('flashcards.batchDeleteTitle', 'Tem certeza que deseja excluir os exercícios selecionados?')}
        message={safeT('flashcards.batchDeleteMessage', 'Você está prestes a excluir {count} cards permanentemente.').replace('{count}', selectedCardsForDeletion.size.toString())}
        onConfirm={handleBatchDelete}
        onCancel={() => setShowConfirmBatchDelete(false)}
        confirmLabel={safeT('common.delete', 'Excluir')}
        isDanger={true}
      />

      {editingDeck ? (
        <div className="flex flex-col gap-6" onScrollCapture={handleScrollEditView}>
          <div className="flex items-center justify-between">
            <AppBackButton
              onClick={() => setEditingDeck(null)}
            />
          </div>
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
            <h2 className="text-3xl font-medium text-ink flex items-center gap-3">
               <span className="text-3xl">{LANGUAGE_FLAGS[editingDeck.langCode] || '🌐'}</span>
               {editingDeck.name.toUpperCase()}
            </h2>
            <div className="flex items-center gap-3">
              <button
                onClick={() => {
                  if (isSelectionMode) {
                    setIsSelectionMode(false);
                    setSelectedCardsForDeletion(new Set());
                  } else {
                    setIsSelectionMode(true);
                  }
                }}
                className={`px-4 py-2 rounded-2xl font-normal text-[10px] uppercase font-mono tracking-[0.14em] transition-all ${isSelectionMode ? 'bg-ink text-surface' : 'bg-slate-100 dark:bg-slate-800 text-slate-500 hover:text-primary'}`}
              >
                {isSelectionMode ? 'Cancelar Seleção' : 'Selecionar'}
              </button>
              {isSelectionMode && selectedCardsForDeletion.size > 0 && (
                <button
                  onClick={() => setShowConfirmBatchDelete(true)}
                  className="p-2 bg-tint-rose dark:bg-tint-rose/30 text-bad rounded-2xl hover:bg-bad hover:text-white transition-all animate-in fade-in zoom-in-95"
                  title="Excluir Selecionados"
                >
                  <Trash2 size={20} />
                </button>
              )}
            </div>
          </div>

          <div className="grid grid-cols-1 gap-4">
             {editingDeck.cards.slice(0, editingVisibleCount).map(card => {
                const isSelected = selectedCardsForDeletion.has(String(card.id));
                return (
                  <div
                    key={card.id}
                    onClick={() => {
                      if (isSelectionMode) {
                        const newSet = new Set(selectedCardsForDeletion);
                        if (newSet.has(String(card.id))) newSet.delete(String(card.id));
                        else if (newSet.size < 20) newSet.add(String(card.id));
                        setSelectedCardsForDeletion(newSet);
                      }
                    }}
                    className={`p-6 md:p-10 rounded-[28px] border transition-all ${isSelectionMode ? 'cursor-pointer hover:scale-[1.01]' : ''} ${isSelected ? 'border-primary bg-primary/5' : 'border-black/[0.06] dark:border-white/[0.06] bg-surface'} relative flex flex-col justify-center items-center text-center`}
                  >
                     {isSelectionMode && (
                        <div className={`absolute top-6 left-6 w-8 h-8 rounded-full border flex items-center justify-center transition-all ${isSelected ? 'border-primary bg-ink text-surface' : 'border-slate-300 dark:border-slate-600'}`}>
                           {isSelected && <Check size={16} />}
                        </div>
                     )}
                     <div className="flex-1 w-full max-w-2xl text-center flex flex-col items-center justify-center space-y-6">
                        <div>
                          <span className="text-[10px] font-normal uppercase font-mono text-primary/40 dark:text-slate-500 tracking-[0.14em] mb-2 block">{safeT('flashcards.original', 'Original')}</span>
                          <p className="text-2xl md:text-3xl font-medium text-ink leading-tight">{card.original}</p>
                        </div>
                        <div className="opacity-30 flex flex-col items-center">
                          <p className="text-[10px] font-normal uppercase tracking-[0.14em] font-mono mb-2">{safeT('flashcards.translation_arrow', 'Tradução ↓')}</p>
                        </div>
                        <div>
                          <p className="text-xl md:text-2xl font-medium text-slate-700 dark:text-slate-300">{card.translation || safeT('flashcards.no_translation', 'Sem tradução')}</p>
                        </div>
                     </div>
                  </div>
                );
             })}
             
             {editingVisibleCount < editingDeck.cards.length && (
               <div className="py-12 flex justify-center">
                  <button onClick={() => setEditingVisibleCount(p => p + 20)} className="px-6 py-3 bg-slate-100 dark:bg-slate-800 text-slate-500 rounded-2xl font-medium text-[13px] hover:text-primary transition-all">Carregar mais</button>
               </div>
             )}
          </div>
        </div>
      ) : (
        <>
      {!selectedLanguageDeck && (
        <AnimatePresence>
          {isDashboardVisible && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 1, ease: 'easeInOut' }}
              style={{ overflow: 'hidden' }}
            >
      <StudyDashboard
        logs={studyLogs}
        availableLangs={(() => { const s = new Set<string>(); allDecks.forEach(d => { if (d.lang_code) s.add(d.lang_code); }); return Array.from(s).map(c => ({ code: c, name: STRICT_LANGUAGES.find(l => l.code === c)?.name || c })); })()}
        availableDecks={allDecks}
        filterLang={filterLang}
        setFilterLang={setFilterLang}
        filterDeck={filterDeck}
        setFilterDeck={setFilterDeck}
        filterPeriod={filterPeriod}
        setFilterPeriod={setFilterPeriod}
        isLoading={isStatsLoading}
        totalReviews={(() => {
          const currentTotal = flashcards.reduce((acc, f) => acc + (f.repetitions || 0), 0);
          const savedTotal = parseInt(localStorage.getItem('libritui_total_reviews_watermark') || '0', 10);
          const displayTotal = Math.max(currentTotal, savedTotal);
          if (currentTotal > savedTotal) {
            localStorage.setItem('libritui_total_reviews_watermark', currentTotal.toString());
          }
          return displayTotal;
        })()}
        profile={profile}
      />
            </motion.div>
          )}
        </AnimatePresence>
      )}

      {importProgress && (
        <div className="flex flex-col items-center justify-center p-8 bg-surface border border-black/[0.06] dark:border-white/[0.06] rounded-2xl mb-8 space-y-4">
          <Loader2 className="w-8 h-8 text-primary animate-spin" />
          <p className="font-normal text-xs text-primary uppercase font-mono tracking-[0.14em] text-center">
            Criando {importProgress.current}/{importProgress.total} cards...<br/>Você pode navegar no aplicativo enquanto preparamos tudo pra você!
          </p>
        </div>
      )}

      {isImporting && !importProgress && (
        <div className="bg-primary/10 border border-primary/20 p-6 rounded-[24px] flex items-center justify-center gap-4 animate-pulse">
          <Loader2 className="animate-spin text-primary" size={24} />
          <p className="font-normal text-xs text-primary uppercase font-mono tracking-[0.14em]">{t('flashcards.processing_anki_wait')}</p>
        </div>
      )}

      {!selectedLanguageDeck && (
        <div className="flex items-center justify-between border-b border-black/[0.06] dark:border-white/[0.06] mb-6 relative">
          <div className="flex">
            <button onClick={() => { setActiveTab('flashcards'); setSelectedLanguageDeck(null); refreshData(); }} className={`text-[13px] font-medium px-6 sm:px-8 h-12 transition-all relative flex items-center gap-2 ${activeTab === 'flashcards' ? 'text-primary' : 'text-slate-400 hover:text-slate-600'}`}>
              <Brain size={14} /> {safeT('flashcards.study', 'Estudar')}
              {activeTab === 'flashcards' && <motion.div layoutId="tab-underline" className="absolute bottom-0 left-0 right-0 h-1 bg-ink rounded-t-full" />}
            </button>
            <button onClick={() => { setActiveTab('favorites'); setSelectedLanguageDeck(null); refreshData(); }} className={`text-[13px] font-medium px-6 sm:px-8 h-12 transition-all relative flex items-center gap-2 ${activeTab === 'favorites' ? 'text-primary' : 'text-slate-400 hover:text-slate-600'}`}>
              <Star size={14} fill={activeTab === 'favorites' ? "currentColor" : "none"} /> {safeT('flashcards.favorites', 'Favoritos')}
              {activeTab === 'favorites' && <motion.div layoutId="tab-underline" className="absolute bottom-0 left-0 right-0 h-1 bg-ink rounded-t-full" />}
            </button>
          </div>

          <div className="relative pb-2" ref={filterDropdownRef}>
            <button
              onClick={() => setIsDeckFilterOpen(!isDeckFilterOpen)}
              className="flex items-center gap-2 px-3 sm:px-4 py-2 rounded-xl bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-200 hover:bg-slate-200 dark:hover:bg-slate-700 font-bold text-xs transition-all"
            >
              <Filter size={14} className="text-primary" />
              <span className="truncate max-w-[100px] sm:max-w-[140px]">
                {selectedDeckFilter === 'all'
                  ? 'Todos os Idiomas'
                  : selectedDeckFilter === 'favorites'
                  ? 'Favoritos'
                  : (LANGUAGE_FLAGS[selectedDeckFilter] ? `${LANGUAGE_FLAGS[selectedDeckFilter]} ${selectedDeckFilter.toUpperCase()}` : selectedDeckFilter.toUpperCase())}
              </span>
              <ChevronDown size={14} className={`transition-transform duration-200 ${isDeckFilterOpen ? 'rotate-180' : ''}`} />
            </button>

            {isDeckFilterOpen && (
              <div className="absolute right-0 top-full mt-1 w-56 max-h-72 overflow-y-auto bg-surface border border-black/[0.06] dark:border-white/[0.06] rounded-2xl shadow-[0_24px_60px_-24px_rgba(0,0,0,0.35)] z-[100] p-2 space-y-1 custom-scrollbar">
                <button
                  onClick={() => { setSelectedDeckFilter('all'); setIsDeckFilterOpen(false); }}
                  className={`w-full text-left px-4 py-2.5 rounded-xl text-xs font-bold transition-all flex items-center gap-2 ${selectedDeckFilter === 'all' ? 'bg-ink text-surface' : 'hover:bg-slate-100 dark:hover:bg-slate-800 text-slate-700 dark:text-slate-300'}`}
                >
                  🌐 Todos os Idiomas
                </button>
                <button
                  onClick={() => { setSelectedDeckFilter('favorites'); setIsDeckFilterOpen(false); }}
                  className={`w-full text-left px-4 py-2.5 rounded-xl text-xs font-bold transition-all flex items-center gap-2 ${selectedDeckFilter === 'favorites' ? 'bg-ink text-surface' : 'hover:bg-slate-100 dark:hover:bg-slate-800 text-slate-700 dark:text-slate-300'}`}
                >
                  ⭐ Favoritos
                </button>
                {availableDeckLanguages.length > 0 && <div className="my-1 border-t border-black/[0.06] dark:border-white/[0.06]" />}
                {availableDeckLanguages.map(langCode => (
                  <button
                    key={langCode}
                    onClick={() => { setSelectedDeckFilter(langCode); setIsDeckFilterOpen(false); }}
                    className={`w-full text-left px-4 py-2.5 rounded-xl text-xs font-bold transition-all flex items-center gap-2 ${selectedDeckFilter === langCode ? 'bg-ink text-surface' : 'hover:bg-slate-100 dark:hover:bg-slate-800 text-slate-700 dark:text-slate-300'}`}
                  >
                    <span>{LANGUAGE_FLAGS[langCode] || '🌐'}</span>
                    <span className="truncate">{langCode.toUpperCase()} ({getLanguageName(langCode)})</span>
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {activeTab === 'flashcards' ? (
        <WatchdogGuard isLoading={isLoadingDecks} onRetry={() => fetchDecks(true)}>
          {isLoadingDecks ? (
            <FlashcardSkeleton />
          ) : selectedLanguageDeck ? (
            <div className="space-y-6">
              <div className="flex items-center gap-3">
                <AppBackButton
                  onClick={() => {
                    if (isGuide) {
                      navigate('/');
                    } else {
                      setSelectedLanguageDeck(null);
                      refreshData();
                    }
                  }}
                />
              </div>
              <h2 className="text-2xl font-medium dark:text-ink flex items-center gap-3">
                <span className="text-3xl">{LANGUAGE_FLAGS[flashcardDecks[selectedLanguageDeck]?.deckData?.lang_code || flashcardDecks[selectedLanguageDeck]?.cards[0]?.lang_code || 'en'] || '🌐'}</span>
                {safeT('flashcards.deckOf', 'Baralho de')} {flashcardDecks[selectedLanguageDeck]?.name?.toUpperCase()}
                <button 
                  onClick={(e) => handleOpenLanguageSelector(e, selectedLanguageDeck)}
                  className="p-2 bg-slate-100 dark:bg-slate-800 text-slate-400 hover:text-primary rounded-xl transition-all ml-2"
                  title="Mudar idioma do sotaque"
                >
                  <Settings size={16} />
                </button>
              </h2>
              <FlashcardGrid
                flashcards={displayedFlashcards}
                onRefresh={refreshData}
                isWordDeck={Boolean(
                  (selectedLanguageDeck && (
                    flashcardDecks[selectedLanguageDeck]?.deckData?.caminho_local_do_arquivo?.includes('new-flashcards') ||
                    flashcardDecks[selectedLanguageDeck]?.name?.toLowerCase().includes('palavra')
                  ))
                )}
                onCardReviewed={(id, newHistory, nextReviewDate) => {
                  setFlashcards(prev => prev.map(f => 
                    f.id.toString() === id.toString() 
                      ? { ...f, repetitions: (f.repetitions || 0) + 1, history: newHistory, next_review_date: nextReviewDate || f.next_review_date } 
                      : f
                  ));
                }}
                onDeckCompleted={async () => {
                  if (user) {
                    await streakService.validateAndIncrementStreak(user.id, 'flashcard');
                    refreshData();
                  }
                  setDeckCompleted(true);
                }}
                onDelete={async (id) => { await flashcardManager.deleteFlashcard(id); await refreshData(); }}
                targetLanguage={targetLanguage}
                currentDeckLanguage={flashcardDecks[selectedLanguageDeck]?.deckData?.lang_code || flashcardDecks[selectedLanguageDeck]?.cards[0]?.lang_code || 'en'}
                audioLoadingId={audioLoadingId}
                setAudioLoadingId={setAudioLoadingId}
              />
            </div>
          ) : Object.keys(flashcardDecks).length === 0 ? (
            <div className="flex flex-col items-center justify-center p-12 text-center bg-surface rounded-[28px] border border-black/[0.06] dark:border-white/[0.06]">
              <Brain size={64} className="text-slate-300 dark:text-slate-700 mb-6" />
              <h3 className="text-xl font-bold text-slate-400">{safeT('flashcards.emptyDecks', 'Nenhum baralho criado')}</h3>
              <p className="text-sm text-slate-500 mt-2">
                {(() => {
                  const currentLang = (i18n.language || 'pt').split('-')[0].toLowerCase();
                  const translations = STORE_TRANSLATIONS[currentLang] || STORE_TRANSLATIONS.pt;
                  return translations.importDirectly || 'Importe baralhos diretamente da ';
                })()}
                <button
                  onClick={() => navigate('/flashcards/store')}
                  className="text-primary hover:underline font-bold"
                >
                  {(() => {
                    const currentLang = (i18n.language || 'pt').split('-')[0].toLowerCase();
                    const translations = STORE_TRANSLATIONS[currentLang] || STORE_TRANSLATIONS.pt;
                    return translations.librituiStore || 'loja Libritui';
                  })()}
                </button>
              </p>
            </div>
          ) : filteredFlashcardDecks.length === 0 ? (
            <div data-tour="flashcards-decks" className="flex flex-col items-center justify-center p-12 text-center bg-surface rounded-[28px] border border-black/[0.06] dark:border-white/[0.06]">
              <Brain size={64} className="text-slate-300 dark:text-slate-700 mb-6" />
              <h3 className="text-xl font-bold text-slate-400"><T k="ui.fc.noDeckFilter" /></h3>
              <button
                onClick={() => setSelectedDeckFilter('all')}
                className="mt-4 px-6 py-3 bg-primary text-slate-900 rounded-2xl font-medium text-[13px] hover:opacity-90 transition-all"
              >
                Ver todos os baralhos
              </button>
            </div>
          ) : (
            <div className="space-y-8">
              <div data-tour="flashcards-decks" className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                {(() => {
                  const totalDeckPages = Math.ceil(filteredFlashcardDecks.length / 4) || 1;
                  const validPage = Math.min(currentDeckPage, totalDeckPages);
                  const visibleDecks = filteredFlashcardDecks.slice((validPage - 1) * 4, validPage * 4);

                  return visibleDecks.map((deckInfo, idx) => {
                    const langCode = deckInfo.cards[0]?.lang_code || deckInfo.deckData?.lang_code || 'en';
                    const isFav = favoriteDecks.includes(deckInfo.id);
                    const { cards, dueCount } = deckInfo;
                    const isDownloading = downloadingDecks[deckInfo.id];
                    
                    return (
                      <div 
                        key={deckInfo.id} 
                        id={idx === 0 ? "tour-flashcard-deck" : undefined} 
                        onClick={() => isDownloading ? null : handleOpenDeck(deckInfo.id)} 
                        className={`group relative bg-surface p-8 text-left transition-all hover: hover:scale-[1.02] overflow-hidden rounded-[28px] border border-black/[0.06] dark:border-white/[0.06] cursor-pointer ${isDownloading ? 'opacity-80 pointer-events-none' : ''}`}
                      >
                        <div className="absolute top-0 right-0 w-32 h-32 bg-primary/5 rounded-full -translate-y-1/2 translate-x-1/2 group-hover:bg-primary/10 transition-colors" />
         
                        {isDownloading && (
                          <div className="absolute inset-0 bg-slate-950/20 dark:bg-slate-950/40 backdrop-blur-sm z-30 flex flex-col items-center justify-center gap-3">
                            <Loader2 className="w-10 h-10 text-primary animate-spin" />
                            <span className="text-[10px] font-normal uppercase font-mono tracking-[0.14em] text-slate-700 dark:text-slate-300">Recuperando...</span>
                          </div>
                        )}

                        <div className="absolute top-6 right-6 z-20">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setOpenDeckMenuId(prev => prev === deckInfo.id ? null : deckInfo.id);
                            }}
                            className="p-2.5 bg-slate-100 dark:bg-slate-800 text-slate-400 hover:text-slate-700 dark:hover:text-white rounded-xl transition-all flex items-center justify-center cursor-pointer"
                            title={i18nInstance.t('ui.fc.deckOptions')}
                            disabled={isDownloading}
                          >
                            <MoreVertical size={18} />
                          </button>

                          {openDeckMenuId === deckInfo.id && (
                            <div 
                              className="absolute right-0 top-full mt-2 w-48 bg-surface border border-black/[0.06] dark:border-white/[0.06] rounded-2xl shadow-[0_24px_60px_-24px_rgba(0,0,0,0.35)] z-50 p-2 space-y-1 animate-in zoom-in-95 duration-150"
                              onClick={(e) => e.stopPropagation()}
                            >
                              <button
                                onClick={(e) => {
                                  setOpenDeckMenuId(null);
                                  handleEditDeck(e, deckInfo.id, deckInfo.name, langCode, cards);
                                }}
                                className="w-full text-left px-4 py-2.5 rounded-xl text-xs font-bold text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800 flex items-center gap-3 transition-colors cursor-pointer"
                              >
                                <Edit3 size={16} className="text-primary" />
                                <span>{safeT('flashcards.editDeck', 'Editar')}</span>
                              </button>

                              <button
                                onClick={(e) => {
                                  setOpenDeckMenuId(null);
                                  toggleFavoriteDeck(e, deckInfo.id);
                                }}
                                className="w-full text-left px-4 py-2.5 rounded-xl text-xs font-bold text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800 flex items-center gap-3 transition-colors cursor-pointer"
                              >
                                <Star size={16} fill={isFav ? "currentColor" : "none"} className={isFav ? "text-warn" : "text-slate-400"} />
                                <span>{isFav ? safeT('flashcards.removeFromFavorites', 'Remover Favorito') : safeT('flashcards.addToFavorites', 'Favoritar')}</span>
                              </button>

                              <button
                                onClick={(e) => handleExportDeckTxt(e, deckInfo)}
                                className="w-full text-left px-4 py-2.5 rounded-xl text-xs font-bold text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800 flex items-center gap-3 transition-colors cursor-pointer"
                              >
                                <Download size={16} className="text-ok" />
                                <span>Importar (.txt)</span>
                              </button>

                              <div className="my-1 border-t border-black/[0.06] dark:border-white/[0.06]" />

                              <button
                                onClick={(e) => {
                                  setOpenDeckMenuId(null);
                                  handleOpenDeleteModal(e, deckInfo.id);
                                }}
                                className="w-full text-left px-4 py-2.5 rounded-xl text-xs font-bold text-bad hover:bg-tint-rose dark:hover:bg-tint-rose/30 flex items-center gap-3 transition-colors cursor-pointer"
                              >
                                <Trash2 size={16} />
                                <span>{safeT('flashcards.deleteDeck', 'Excluir')}</span>
                              </button>
                            </div>
                          )}
                        </div>
         
                        <div className="relative z-10 space-y-6">
                          <div className="relative w-16 h-16 flex items-center justify-center">
                            <div className="text-5xl">{LANGUAGE_FLAGS[langCode] || '🌐'}</div>
                            {isDownloading && (
                              <div className="absolute inset-0 rounded-full border-4 border-t-primary border-r-transparent border-b-transparent border-l-transparent animate-spin" />
                            )}
                          </div>
                          <div>
                            <h3 className="text-2xl font-medium dark:text-ink tracking-[-0.03em]">{deckInfo.name.toUpperCase()}</h3>
                            <p className="text-[10px] font-normal uppercase tracking-[0.14em] font-mono text-slate-400 mt-1">
                              {isDownloading ? 'Baixando...' : `${cards.length} ${safeT('flashcards.cards', 'Cartões')}`}
                            </p>
                          </div>
                          <div className="flex items-center justify-between pt-4 border-t border-slate-50 dark:border-white/[0.06]">
                            {isDownloading ? (
                              <span className="text-xs font-normal text-slate-400 flex items-center gap-1 uppercase font-mono tracking-[0.14em] animate-pulse">Carregando...</span>
                            ) : dueCount > 0 ? (
                              <span className="text-lg font-semibold text-primary">{dueCount} {safeT('flashcards.reviews', 'revisões')}</span>
                            ) : cards.length === 0 ? (
                              <span className="text-xs font-normal text-slate-400 flex items-center gap-1 uppercase font-mono tracking-[0.14em]"><T k="ui.fc.zeroCards" /></span>
                            ) : (
                              <span className="text-xs font-normal text-warn flex items-center gap-1 uppercase font-mono tracking-[0.14em]">{safeT('flashcards.completed', 'Concluído')} 🔥</span>
                            )}
                            <div className="w-10 h-10 rounded-full bg-primary/10 text-primary flex items-center justify-center group-hover:bg-primary group-hover:text-white transition-all"><ChevronRight size={20} /></div>
                          </div>
                        </div>
                      </div>
                    );
                  });
                })()}
              </div>

              {/* Paginação de Baralhos (4 por vez) */}
              {Math.ceil(filteredFlashcardDecks.length / 4) > 1 && (
                <div className="flex items-center justify-center gap-4 pt-4">
                  <button
                    disabled={currentDeckPage <= 1}
                    onClick={() => setCurrentDeckPage(prev => Math.max(prev - 1, 1))}
                    className="flex items-center gap-2 px-5 py-2.5 rounded-2xl bg-surface border border-black/[0.06] dark:border-white/[0.06] text-[13px] font-medium text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800 disabled:opacity-40 disabled:cursor-not-allowed transition-all cursor-pointer"
                  >
                    <ChevronLeft size={16} />
                    <span>Anterior</span>
                  </button>
                  
                  <span className="text-xs font-bold text-slate-500 dark:text-slate-400">
                    Página {Math.min(currentDeckPage, Math.ceil(filteredFlashcardDecks.length / 4))} de {Math.ceil(filteredFlashcardDecks.length / 4)}
                  </span>

                  <button
                    disabled={currentDeckPage >= Math.ceil(filteredFlashcardDecks.length / 4)}
                    onClick={() => setCurrentDeckPage(prev => Math.min(prev + 1, Math.ceil(filteredFlashcardDecks.length / 4)))}
                    className="flex items-center gap-2 px-5 py-2.5 rounded-2xl bg-surface border border-black/[0.06] dark:border-white/[0.06] text-[13px] font-medium text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800 disabled:opacity-40 disabled:cursor-not-allowed transition-all cursor-pointer"
                  >
                    <span><T k="ui.common.next" /></span>
                    <ChevronRight size={16} />
                  </button>
                </div>
              )}
            </div>
          )}
        </WatchdogGuard>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {favoriteDecksList.length === 0 ? (
            <div className="col-span-full border border-dashed border-black/[0.06] dark:border-white/[0.06] rounded-[28px] p-20 text-center flex flex-col items-center justify-center space-y-6 bg-slate-50/50 dark:bg-slate-900/20">
              <Star size={64} className="text-slate-300 dark:text-slate-700" />
              <h3 className="text-xl font-bold text-ink dark:text-slate-200">{safeT('flashcards.emptyFavorites', 'Você ainda não tem baralhos favoritos')}</h3>
            </div>
          ) : (
            favoriteDecksList.map((deckInfo) => {
              const langCode = deckInfo.cards[0]?.lang_code || deckInfo.deckData?.lang_code || 'en';
              const { dueCount, cards } = deckInfo as any;
              const isDownloading = downloadingDecks[deckInfo.id];
              
              return (
                <div 
                  key={deckInfo.id} 
                  onClick={() => isDownloading ? null : setSelectedLanguageDeck(deckInfo.id)} 
                  className={`group relative bg-surface border border-primary/20 rounded-[28px] p-6 text-left transition-all hover: hover:scale-[1.02] overflow-hidden cursor-pointer ${isDownloading ? 'opacity-80 pointer-events-none' : ''}`}
                >
                  {isDownloading && (
                    <div className="absolute inset-0 bg-slate-950/20 dark:bg-slate-950/40 backdrop-blur-sm z-30 flex items-center justify-center">
                      <Loader2 className="w-6 h-6 text-primary animate-spin" />
                    </div>
                  )}
                  <div className="flex items-center gap-4">
                    <div className="relative w-12 h-12 flex items-center justify-center">
                      <div className="text-4xl">{LANGUAGE_FLAGS[langCode] || '🌐'}</div>
                      {isDownloading && (
                        <div className="absolute inset-0 rounded-full border border-t-primary border-r-transparent border-b-transparent border-l-transparent animate-spin" />
                      )}
                    </div>
                    <div className="min-w-0 flex-1">
                      <h3 className="font-semibold text-lg truncate">{deckInfo.name.toUpperCase()}</h3>
                      {isDownloading ? (
                        <p className="text-[9px] font-normal uppercase tracking-[0.14em] font-mono text-slate-400 flex items-center gap-1">Baixando...</p>
                      ) : dueCount > 0 ? (
                        <p className="text-[9px] font-normal uppercase tracking-[0.14em] font-mono text-primary">{dueCount} {safeT('flashcards.reviewsPending', 'revisões pendentes')}</p>
                      ) : cards.length === 0 ? (
                        <p className="text-[9px] font-normal uppercase tracking-[0.14em] font-mono text-slate-400 flex items-center gap-1"><T k="ui.fc.zeroCards" /></p>
                      ) : (
                        <p className="text-[9px] font-normal uppercase tracking-[0.14em] font-mono text-warn flex items-center gap-1">{safeT('flashcards.completed', 'Concluído')} 🔥</p>
                      )}
                    </div>
                    <div className="p-2 bg-primary/10 text-primary rounded-xl group-hover:bg-primary group-hover:text-white transition-all"><ChevronRight size={16} /></div>
                  </div>
                </div>
              );
            })
          )}
        </div>
      )}
      </>
    )}

      {/* All Reviews Done Modal */}
      <TxtImportModal
        isOpen={showTxtImportModal}
        onClose={() => setShowTxtImportModal(false)}
        onConfirm={handleTxtImportConfirm}
        existingDecks={Object.values(flashcardDecks).map(d => ({ id: d.id, name: d.name }))}
      />

      {allReviewedDeck && (
        <div
          className="fixed inset-0 z-[200] flex items-center justify-center p-6 bg-black/80 backdrop-blur-md animate-in fade-in duration-200"
          onClick={() => {
            setAllReviewedDeck(null);
            setSelectedLanguageDeck(allReviewedDeck);
          }}
        >
          <div
            className="bg-surface p-8 rounded-[28px] border border-ok/30 w-[85vw] max-w-md animate-in zoom-in-95 duration-200 flex flex-col items-center text-center gap-6"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="w-16 h-16 rounded-3xl bg-ok/15 text-ok flex items-center justify-center">
              <Check size={32} strokeWidth={2.5} />
            </div>
            <div className="space-y-3">
              <h3 className="text-xl font-medium text-ink tracking-[-0.02em]"><T k="ui.common.congrats" /></h3>
              <p className="text-sm text-ok dark:text-ok font-semibold leading-relaxed">
                Todos os cards deste baralho já foram revisados!<br />
                Aguarde algumas horas até a próxima revisão ou continue estudando para reforçar o aprendizado.
              </p>
            </div>
            <button
              onClick={() => {
                setSelectedLanguageDeck(allReviewedDeck);
                setAllReviewedDeck(null);
              }}
              className="text-[13px] font-medium w-full h-12 bg-ok text-surface rounded-full hover:bg-ok active:scale-95 transition-all"
            >
              Continuar Estudando
            </button>
          </div>
        </div>
      )}

      {/* Modal de Seleção de Idioma do Baralho */}
      {isLanguageSelectorOpen && (
        <div className="fixed inset-0 z-[250] flex items-center justify-center p-6 bg-slate-950/60 backdrop-blur-sm animate-in fade-in duration-200">
          <div className="bg-surface p-8 rounded-[28px] border border-black/[0.06] dark:border-white/[0.06] w-[90vw] max-w-lg max-h-[80vh] overflow-hidden flex flex-col animate-in zoom-in-95 duration-200">
            <h3 className="text-xl font-medium text-ink tracking-[-0.02em] mb-2">{safeT('flashcards.deckLanguageTitle', 'Idioma do Baralho')}</h3>
            <p className="text-sm text-slate-500 dark:text-slate-400 mb-6 font-medium">{safeT('flashcards.deckLanguageDesc', 'Selecione o idioma deste baralho para garantir a pronúncia correta (TTS).')}</p>
            
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 overflow-y-auto pr-2 custom-scrollbar pb-4">
              {Object.entries(ISO_TO_LOCALE).map(([code, locale]) => (
                <button
                  key={code}
                  onClick={() => saveDeckLanguage(code)}
                  className={`p-4 rounded-2xl border flex flex-col items-center gap-2 transition-all ${
                    (deckLanguageMappings[deckBeingConfigured || ''] === code)
                      ? 'bg-primary/10 border-primary text-primary'
                      : 'bg-slate-50 dark:bg-slate-800/50 border-transparent hover:border-slate-200 dark:hover:border-slate-700'
                  }`}
                >
                  <span className="text-3xl">{LANGUAGE_FLAGS[code] || '🌐'}</span>
                  <span className="text-[10px] font-normal uppercase font-mono tracking-[0.14em] truncate w-full text-center">
                    {code.toUpperCase()} ({locale})
                  </span>
                </button>
              ))}
            </div>

            <button
              onClick={() => setIsLanguageSelectorOpen(false)}
              className="text-[13px] font-medium mt-6 w-full h-12 bg-tint-stone/60 text-muted hover:text-ink rounded-full hover:bg-tint-stone transition-all"
            >
              {safeT('common.close', 'Fechar')}
            </button>
          </div>
        </div>
      )}

      {/* Modal de Confirmação de Exclusão de Baralho */}
      <ConfirmationModal
        isOpen={!!deckToDelete}
        title={safeT('flashcards.deleteDeckTitle', 'Excluir baralho')}
        message={safeT('flashcards.deleteDeckMessage', 'Tem certeza que deseja excluir este baralho? Esta ação não pode ser desfeita.')}
        confirmLabel={safeT('common.delete', 'Excluir')}
        isDanger
        onConfirm={executeDeleteDeck}
        onCancel={() => setDeckToDelete(null)}
      />

      {/* Modal de Renomeação de Baralho */}
      <InputModal
        isOpen={!!deckToRename}
        title={t('flashcards.rename_deck.title')}
        placeholder={t('flashcards.rename_deck.placeholder')}
        initialValue={deckToRename || ''}
        onSave={executeRenameDeck}
        onCancel={() => setDeckToRename(null)}
      />

      {appNotification.show && (
        <div className="fixed inset-0 z-[300] flex items-center justify-center p-6 bg-slate-950/60 backdrop-blur-sm animate-in fade-in duration-200" onClick={() => setAppNotification(prev => ({ ...prev, show: false }))}>
          <div className="bg-surface p-8 rounded-[28px] border border-black/[0.06] dark:border-white/[0.06] w-[80vw] max-w-sm animate-in zoom-in-95 duration-200 flex flex-col items-center text-center gap-6" onClick={(e) => e.stopPropagation()}>
            <div className={`w-14 h-14 rounded-full flex items-center justify-center ${appNotification.type === 'error' ? 'bg-tint-rose text-ink' : 'bg-tint-sage text-ink'}`}>
              {appNotification.type === 'error' ? <AlertTriangle size={22} strokeWidth={1.75} /> : <Check size={22} strokeWidth={1.75} />}
            </div>
            <div className="space-y-2">
              <h3 className="text-xl font-medium text-ink tracking-[-0.02em]">{appNotification.title}</h3>
              {appNotification.message && <p className="text-sm text-slate-500 dark:text-slate-400 font-medium leading-relaxed">{appNotification.message}</p>}
            </div>
            <button
              onClick={() => setAppNotification(prev => ({ ...prev, show: false }))}
              className={`text-[13px] font-medium w-full h-12 text-white rounded-full transition-all hover:opacity-90 active:scale-95 ${appNotification.type === 'error' ? 'bg-bad' : 'bg-ink'}`}
            >
              OK
            </button>
          </div>
        </div>
      )}

      {/* Deck Completed Modal — shown after user gives feedback on the last card */}
      {deckCompleted && (
        <div className="fixed inset-0 z-[250] flex items-center justify-center p-6 bg-black/80 backdrop-blur-md animate-in fade-in duration-200">
          <div className="bg-surface p-8 rounded-[28px] border border-ok/30 w-[85vw] max-w-md animate-in zoom-in-95 duration-200 flex flex-col items-center text-center gap-6">
            <div className="w-16 h-16 rounded-3xl bg-ok/15 text-ok flex items-center justify-center">
              <Check size={32} strokeWidth={2.5} />
            </div>
            <div className="space-y-3">
              <h3 className="text-xl font-medium text-ink tracking-[-0.02em]"><T k="ui.common.congrats" /></h3>
              <p className="text-sm text-ok dark:text-ok font-semibold leading-relaxed">
                Você concluiu este baralho!<br />
                Volte mais tarde para revisá-lo e reforçar o aprendizado.
              </p>
            </div>
            <button
              onClick={() => {
                setDeckCompleted(false);
                setSelectedLanguageDeck(null);
                refreshData();
              }}
              className="text-[13px] font-medium w-full h-12 bg-ok text-surface rounded-full hover:bg-ok active:scale-95 transition-all"
            >
              Confirmar
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default FlashcardsPage;
